import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ClipboardPaste,
  FileDown,
  FileText,
  ImagePlus,
  Info,
  IndentDecrease,
  IndentIncrease,
  Loader2,
  Plus,
  Save,
  Search,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  Wand2,
} from 'lucide-react';
import {
  DESCRICAO_ELEMENTO,
  DESCRICAO_GRUPO_DOCUMENTO,
  DESCRICAO_ESTILO_DE_CABECALHO,
  DESCRICAO_ESTILO_DE_RODAPE,
  DESCRICAO_FORMATO_DE_ANOTACAO,
  DESCRICAO_FONTE_DO_DOCUMENTO,
  DESCRICAO_PAPEL_DE_DOCUMENTO,
  DESCRICAO_POSICAO_DA_ANOTACAO,
  DESCRICAO_POSICAO_DO_NUMERO,
  DESCRICAO_ALINHAMENTO_DE_CAPITULO,
  DESCRICAO_ROTULO_DE_CAPITULO,
  DESCRICAO_FORMATO_DE_PARAGRAFO,
  ESTILOS_DE_CABECALHO,
  ESTILOS_DE_RODAPE,
  FONTES_DA_FAIXA,
  FORMATOS_DE_ANOTACAO,
  FORMATOS_DE_PARAGRAFO,
  ROTULO_FORMATO_DE_PARAGRAFO,
  GRUPO_DE_ELEMENTO_DOCUMENTO,
  MAXIMO_FOLHAS_DE_ANOTACAO,
  PAPEIS_DE_DOCUMENTO,
  POSICOES_DA_ANOTACAO,
  POSICOES_DA_FAIXA,
  POSICOES_DO_NUMERO,
  ALINHAMENTOS_DE_CAPITULO,
  ROTULOS_DE_CAPITULO,
  ROTULO_ESTILO_DE_CABECALHO,
  ROTULO_ESTILO_DE_RODAPE,
  ROTULO_FONTE_DA_FAIXA,
  ROTULO_FORMATO_DE_ANOTACAO,
  FONTES_DO_DOCUMENTO,
  ROTULO_FONTE_DO_DOCUMENTO,
  ROTULO_PAPEL_DE_DOCUMENTO,
  fonteDoDocumento,
  ROTULO_POSICAO_DA_FAIXA,
  ROTULO_POSICAO_DO_NUMERO,
  ROTULO_POSICAO_DA_ANOTACAO,
  ROTULO_ALINHAMENTO_DE_CAPITULO,
  ROTULO_ROTULO_DE_CAPITULO,
  estiloDaFaixa,
  estiloDaFaixaPadrao,
  formatoDoCapitulo,
  formatoDeParagrafo,
  folhaDoDocumento,
  geometriaDoDocumento,
  geometriaDoPapel,
  molduraDaFolha,
  paginasDoSumario,
  papelDoDocumento,
  posicaoDaAnotacao,
  papelEscolhivel,
  DEGRAU_DE_RECUO_MM,
  degrauDeRecuo,
  degrausDeRecuoDoPapel,
  ELEMENTOS_DOCUMENTO,
  ELEMENTOS_POR_MODELO,
  GRUPOS_DOCUMENTO,
  MODELOS_DE_DOCUMENTO,
  ROTULO_ELEMENTO,
  ROTULO_GRUPO_DOCUMENTO,
  ROTULO_MODELO_DE_DOCUMENTO,
  CAMPOS_DA_REFERENCIA,
  ROTULO_TIPO_DE_REFERENCIA,
  ROTULO_TIPO_DE_TRABALHO,
  NORMAS,
  ROTULO_NORMA,
  DESCRICAO_NORMA,
  RESUMO_DA_NORMA,
  TIPOS_DE_REFERENCIA,
  TIPOS_DE_TRABALHO,
  aplicarHtmlAoDocumento,
  aplicarTextoEstruturado,
  blocosDaSecao,
  citacaoDaReferencia,
  documentoParaHtml,
  documentoEmMarkdown,
  DESCRICAO_POSICAO_DAS_NOTAS,
  LARGURA_DE_IMAGEM_MM,
  POSICOES_DAS_NOTAS,
  ROTULO_POSICAO_DAS_NOTAS,
  elementoObrigatorio,
  numerosDasNotas,
  posicaoDasNotas,
  formatacaoPadrao,
  itensDoModelo,
  lerTextoColado,
  marcarReferenciasRepetidas,
  linhaDaReferencia,
  listaDaNorma,
  normaDoDocumento,
  normaNumerada,
  proximoNumero,
  numerarSecoes,
  referenciasEmOrdem,
} from '@miolos/core';
import type {
  AvisoDocumento,
  BlocoDeTexto,
  CampoDaReferencia,
  FonteDoDocumento,
  PosicaoDasNotas,
  NormaDeCitacao,
  CaixaDaFolha,
  DocumentoAcademico,
  ElementoDocumento,
  EstiloDaFaixa,
  FiguraDocumento,
  FolhaDoDocumento,
  FonteDaFaixa,
  FormatoDeAnotacao,
  FormatoDeCapitulo,
  FormatoDeParagrafo,
  GrupoDocumento,
  PosicaoDaAnotacao,
  ModeloDeDocumento,
  ReferenciaDocumento,
  ReferenciaLida,
  SecaoDocumento,
  TextoEstruturado,
  TipoDeBloco,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GrupoDoMenu, Menu } from '@/components/ui/menu';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, ErroApi, urlDaImagem } from '@/lib/api';
import { DocumentoImpresso } from '@/documento/DocumentoImpresso';
import { EditorDeTexto } from '@/documento/EditorDeTexto';
import type { EditorDeTextoHandle } from '@/documento/EditorDeTexto';
import { cssDaFolha, estiloDaFaixaEmCss, familiaDoDocumento } from '@/documento/folha';
import { baixarDocumentoDocx, nomeDoArquivoDocx } from '@/documento/docx';
import { TradutorIa } from '@/documento/TradutorIa';
import { PainelPerfil } from '@/documento/PainelPerfil';
import { comMarcas } from '@/documento/marcas';
import { Grade } from '@/wizard/ui';

/** Abas do editor: cada uma responde por um pedaço do documento. */
const ABAS = ['ITENS', 'IDENTIFICACAO', 'RESUMO', 'TEXTO', 'FOLHA', 'FIGURAS', 'REFERENCIAS', 'ANEXOS', 'IMAGENS'] as const;
type Aba = (typeof ABAS)[number];

const ROTULO_ABA: Record<Aba, string> = {
  ITENS: 'Itens do documento',
  IDENTIFICACAO: 'Identificação',
  RESUMO: 'Resumo e abstract',
  /* No livro a mesma aba guarda apresentação, dedicatória, epígrafe e fecho. */
  TEXTO: 'Texto',
  FOLHA: 'Folha e capítulos',
  FIGURAS: 'Figuras',
  REFERENCIAS: 'Referências',
  ANEXOS: 'Anexos',
  IMAGENS: 'Capa e cabeçalho',
};

/**
 * As partes do documento, na ordem em que a norma as apresenta.
 *
 * É a **espinha da tela**: cada parte é uma linha da coluna da esquerda e a que
 * está aberta ocupa o lugar do texto, no meio. A ordem é a da ABNT (pré-textuais,
 * textuais, pós-textuais) — quem procura "onde eu escrevo o resumo?" procura ali,
 * e não num menu escondido.
 */
const PARTES_DO_DOCUMENTO: Array<{
  aba: Aba;
  rotulo: string;
  /** No livro a mesma parte tem outro nome. */
  rotuloLivro?: string;
  /** No artigo a mesma parte tem outro nome (não há capa para encadernar). */
  rotuloArtigo?: string;
  dica: string;
}> = [
  {
    aba: 'TEXTO',
    rotulo: 'O texto',
    dica: 'Onde se escreve: parágrafos, listas, citações e tabelas.',
  },
  {
    aba: 'IDENTIFICACAO',
    rotulo: 'Capa e folha de rosto',
    rotuloArtigo: 'Título e autoria',
    dica: 'Título, autor, instituição, orientador e ano.',
  },
  {
    aba: 'RESUMO',
    rotulo: 'Resumo e abstract',
    rotuloLivro: 'Abertura e fecho',
    dica: 'O resumo com as palavras-chave, e o abstract.',
  },
  {
    aba: 'ITENS',
    rotulo: 'Peças do trabalho',
    dica: 'Dedicatória, agradecimentos, epígrafe, listas, apêndices e anexos.',
  },
  {
    aba: 'FOLHA',
    rotulo: 'Folha e seções',
    rotuloLivro: 'Folha e capítulos',
    dica: 'Numeração de página, margens, abertura de capítulo e folhas de anotação.',
  },
  {
    aba: 'FIGURAS',
    rotulo: 'Figuras',
    dica: 'As figuras com legenda e fonte, na ordem em que saem no texto.',
  },
  {
    aba: 'REFERENCIAS',
    rotulo: 'Referências',
    dica: 'A lista de obras — é dela que saem as citações do texto.',
  },
  {
    aba: 'ANEXOS',
    rotulo: 'Anexos',
    dica: 'Envie os PDFs de documentos que embasam o trabalho: norma, laudo, contrato.',
  },
  {
    aba: 'IMAGENS',
    rotulo: 'Capa e cabeçalho',
    rotuloArtigo: 'Cabeçalho das folhas',
    dica: 'A arte da capa e a imagem que se repete no alto das folhas.',
  },
];

/**
 * Onde cada peça do documento se escreve.
 *
 * A coluna da esquerda lista as peças do modelo — capa, folha de rosto, resumo,
 * sumário, capítulos, referências... — e cada uma leva ao painel onde ela é
 * preenchida, que é sempre um dos painéis do meio. É o mapa entre os dois: o autor
 * procura "onde escrevo a dedicatória?" e chega lá sem saber de abas.
 *
 * Peça que **não tem texto próprio** (o sumário, que o motor monta das seções; o
 * glossário e os anexos, que são folha com o título) fica fora do mapa de propósito:
 * aparece na lista para ser ligada e desligada, e a dica diz que ali não há o que
 * escrever. Melhor do que abrir um painel que não é dela.
 */
const PARTE_DA_PECA: Partial<Record<ElementoDocumento, Aba>> = {
  CAPA: 'IDENTIFICACAO',
  FOLHA_DE_ROSTO: 'IDENTIFICACAO',
  FOLHA_DE_APROVACAO: 'IDENTIFICACAO',
  DEDICATORIA: 'RESUMO',
  AGRADECIMENTOS: 'RESUMO',
  EPIGRAFE: 'RESUMO',
  RESUMO: 'RESUMO',
  ABSTRACT: 'RESUMO',
  RESUMO_DO_ARTIGO: 'RESUMO',
  LISTA_DE_ABREVIATURAS: 'RESUMO',
  APRESENTACAO: 'RESUMO',
  EPILOGO: 'RESUMO',
  SOBRE_O_AUTOR: 'RESUMO',
  LISTA_DE_FIGURAS: 'FIGURAS',
  LISTA_DE_TABELAS: 'FIGURAS',
  CAPITULOS: 'TEXTO',
  REFERENCIAL_TEORICO: 'TEXTO',
  METODOLOGIA: 'TEXTO',
  RESULTADOS: 'TEXTO',
  CONSIDERACOES_FINAIS: 'TEXTO',
  REFERENCIAS: 'REFERENCIAS',
  ANEXOS: 'ANEXOS',
  APENDICES: 'ANEXOS',
  PAGINA_DE_ANOTACAO: 'FOLHA',
};

/**
 * O nome da peça no livro, quando o livro a chama de outro jeito.
 *
 * O livro não tem contracapa nem índice como peças separadas: o índice é o sumário
 * que o motor monta das seções, e a contracapa é a página de quem escreveu. Mostrar
 * os dois nomes é o que faz o autor achar o que procura — o recurso e a norma são os
 * mesmos, só o nome é que muda de um tipo de livro para o outro.
 */
const ROTULO_DA_PECA_LIVRO: Partial<Record<ElementoDocumento, string>> = {
  SUMARIO: 'Sumário (índice)',
  SOBRE_O_AUTOR: 'Sobre o autor (contracapa)',
};

/**
 * As peças que compõem um grupo, **no modelo escolhido**.
 *
 * Uma regra só para as duas listas de peças da tela (a coluna da esquerda e o cartão
 * "Peças do trabalho"): o artigo não mostra capa nem folha de rosto porque não tem,
 * e o livro não mostra resumo nem referencial teórico. Se a regra ficassem em dois
 * lugares, uma das listas acabaria mostrando o que a outra esconde.
 *
 * O `RESUMO_DO_ARTIGO` sai fora dos outros modelos porque ele é o resumo do artigo,
 * que já aparece como `RESUMO`/`ABSTRACT` no trabalho e como abertura no livro.
 */
function pecasDoGrupo(
  modelo: ModeloDeDocumento,
  permitidos: ElementoDocumento[],
  grupo: GrupoDocumento,
): ElementoDocumento[] {
  return ELEMENTOS_DOCUMENTO.filter(
    (item) =>
      permitidos.includes(item) &&
      GRUPO_DE_ELEMENTO_DOCUMENTO[item] === grupo &&
      !(item === 'RESUMO_DO_ARTIGO' && modelo !== 'ARTIGO_CIENTIFICO'),
  );
}

/**
 * Editor de documento acadêmico.
 *
 * Três decisões que valem para a tela inteira:
 *
 * 1. **A prévia é o documento de verdade** — o mesmo componente que imprime
 *    (`DocumentoImpresso`), só menor. Não existe "desenho da prévia": se algo
 *    aparece aqui, sai no papel igual.
 * 2. **Nada de salvar a cada tecla.** O documento é grande e o texto é do autor:
 *    salva quando ele manda, e o botão avisa quando há mudança pendente.
 * 3. **Quem manda no que entra é a seleção de itens** — a mesma função do motor
 *    (`itensDoModelo`), então o artigo não ganha capa e o trabalho não perde a
 *    folha de rosto.
 */
export function EditorDocumento() {
  const { id = '' } = useParams();
  const navegar = useNavigate();

  const [nome, setNome] = useState('');
  const [documento, setDocumento] = useState<DocumentoAcademico | null>(null);
  const [avisos, setAvisos] = useState<AvisoDocumento[]>([]);
  const [aba, setAba] = useState<Aba>('TEXTO');
  const [erro, setErro] = useState('');
  /** O que a gravação recusou, campo por campo (vazio quando deu tudo certo). */
  const [camposDoErro, setCamposDoErro] = useState<string[]>([]);
  const [estado, setEstado] = useState<'carregando' | 'pronto' | 'salvando' | 'salvo'>('carregando');
  const [imprimindo, setImprimindo] = useState(false);
  const mudou = useRef(false);
  /**
   * De onde veio a última mudança do texto: **do editor** (a pessoa escreveu) ou
   * **da tela** (abriu o documento, mexeu no painel, desfez). É o que decide se o
   * HTML é reinjetado dentro do editor — reinjetar o que ele mesmo acabou de
   * escrever jogaria o cursor para o começo a cada tecla.
   */
  const fonteDaMudanca = useRef<'editor' | 'tela'>('tela');

  /* ---------------- Carregar ---------------- */
  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const resposta = await api.abrirDocumento(id);
        if (!vivo) return;
        setNome(resposta.documento.nome);
        setDocumento(resposta.documento.dados);
        setAvisos(resposta.avisos);
        setEstado('pronto');
      } catch (causa) {
        if (!vivo) return;
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir o documento.');
        setEstado('pronto');
      }
    })();
    return () => {
      vivo = false;
    };
  }, [id]);

  /**
   * Toda edição passa por aqui: marca que há mudança para salvar.
   *
   * `origem` diz **de onde** veio a mudança do texto. O padrão é a tela (painel,
   * importação, desfazer), que é o que faz o editor ser atualizado por fora; o
   * próprio editor se anuncia como `'editor'`, e aí o texto **não** volta para
   * dentro dele — é o que mantém o cursor onde a pessoa está escrevendo.
   */
  const editar = useCallback(
    (mudanca: (atual: DocumentoAcademico) => DocumentoAcademico, origem: 'tela' | 'editor' = 'tela') => {
    fonteDaMudanca.current = origem;
    setDocumento((atual) => (atual ? mudanca(atual) : atual));
    mudou.current = true;
    setEstado('pronto');
    },
    [],
  );

  /* ---------------- Escrita no editor ---------------- */

  /** O que o editor recebe: o documento em HTML (a conversão é do motor). */
  const htmlDoDocumento = useMemo(() => (documento ? documentoParaHtml(documento) : ''), [documento]);

  /**
   * O que o editor escreveu, de volta para o documento.
   *
   * O caminho é: HTML → seções e blocos (`aplicarHtmlAoDocumento`, no motor) →
   * desenho do papel logo abaixo. Os ids das seções são reaproveitados, então
   * mexer no texto não perde a configuração de folha de cada seção.
   */
  const aoMudarHtml = useCallback(
    (html: string) => {
      editar((atual) => aplicarHtmlAoDocumento(atual, html), 'editor');
    },
    [editar],
  );

  /*
   * `versaoDoTexto` diz ao editor **quando** reinjetar o HTML: só quando o
   * documento mudou por fora dele (abrir, painel, resposta do servidor). Sem esse
   * aviso, cada tecla devolveria o HTML ao editor e o cursor pularia para o começo
   * do texto — o defeito clássico de editor com prévia.
   */
  const [versaoDoTexto, setVersaoDoTexto] = useState(0);
  const htmlAnterior = useRef('');
  useEffect(() => {
    if (htmlDoDocumento === htmlAnterior.current) return;
    htmlAnterior.current = htmlDoDocumento;
    if (fonteDaMudanca.current !== 'editor') setVersaoDoTexto((atual) => atual + 1);
  }, [htmlDoDocumento]);

  /** O nível da seção em que o cursor está: é o que marca Título 1 a 6 na barra. */
  const [nivelDoCursor, setNivelDoCursor] = useState('h1');
  /**
   * O recuo do bloco em que o cursor está, em degraus. `null` = o cursor está num
   * título, que não aceita recuo — é o que desabilita os dois botões da barra.
   */
  const [recuoDoCursor, setRecuoDoCursor] = useState<number | null>(null);
  const editorDeTexto = useRef<EditorDeTextoHandle>(null);

  /*
   * A altura da área de digitação acompanha a janela: numa tela de notebook ela
   * precisa caber junto com as barras e com o papel da direita, e numa tela grande
   * seria desperdício ficar pequena. O editor ainda pode ser esticado à mão (o
   * TinyMCE deixa).
   */
  const [alturaDoEditor] = useState(() =>
    Math.min(760, Math.max(320, Math.round((typeof window === 'undefined' ? 900 : window.innerHeight) - 320))),
  );

  /** Quantas notas o documento já tem — é o que dá o número da próxima. */
  const proximoNumeroDeNota = useMemo(
    () => (documento ? numerosDasNotas(documento).size + 1 : 1),
    [documento],
  );

  /**
   * Envia a imagem escolhida na barra e a põe no texto, no lugar do cursor.
   *
   * É o **mesmo caminho** das imagens de capa e de cabeçalho (o mesmo envio, a mesma pasta):
   * a figura do texto não tem regra própria de arquivo. O envio pode falhar (tipo ou
   * tamanho), e aí o aviso é o mesmo dos outros erros da tela — sem figura e sem susto.
   */
  async function enviarImagemParaOTexto(arquivo: File) {
    try {
      const { arquivo: enviado } = await api.enviarImagem(arquivo);
      editorDeTexto.current?.inserirImagem(enviado.url);
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível enviar a imagem.');
    }
  }

  /**
   * Uma seção do sumário leva para o texto: abre a parte **O texto** e manda o editor
   * para o título daquela seção.
   */
  const irParaASecao = (secao: SecaoDocumento) => {
    setAba('TEXTO');
    editorDeTexto.current?.irParaSecao(secao.titulo);
  };

  /* A norma escolhida para o documento (ausente = ABNT): manda na linha e na citação. */
  const norma = normaDoDocumento(documento);

  /**
   * A citação autor-data entra **onde o cursor está**.
   *
   * O texto sai do motor (`citacaoAutorData`): é a mesma função que decide o
   * sobrenome da referência impressa, então a lista e o texto não podem divergir —
   * e a conferência da norma (citação sem referência, referência sem citação) só
   * faz sentido porque as duas pontas saem da mesma regra.
   */
  const citar = (referencia: ReferenciaDocumento, pagina: string) => {
    /*
     * Nas normas numeradas, a **primeira citação dá o número**: quem é citado primeiro
     * recebe o 1, e é isso que põe a lista na ordem em que as obras aparecem no texto
     * (a regra do IEEE e do Vancouver). O número fica gravado na referência, e pode ser
     * corrigido na ficha dela.
     */
    let alvo = referencia;
    if (normaNumerada(norma) && !referencia.numeroDaLista) {
      /*
       * O número é o **mesmo que a lista e o menu mostram** (a lista numerada provisória,
       * na ordem em que as obras foram cadastradas): assim o que a pessoa leu é o que a
       * citação escreve, e nada muda de lugar depois do clique.
       */
      const daLista = listaDaNorma(documento?.referencias ?? [], norma).find(
        (item) => item.referencia.id === referencia.id,
      );
      const numeroDaLista = daLista?.numero ?? proximoNumero(documento?.referencias ?? []);
      alvo = { ...referencia, numeroDaLista };
      editar((atual) => ({
        ...atual,
        referencias: atual.referencias.map((item) =>
          item.id === referencia.id ? { ...item, numeroDaLista } : item,
        ),
      }));
    }

    const texto = citacaoDaReferencia(alvo, norma, pagina);
    if (texto) editorDeTexto.current?.inserirNoCursor(texto);
  };

  const salvar = useCallback(
    async (silencioso = false) => {
      if (!documento) return;
      setEstado('salvando');
      try {
        const resposta = await api.salvarDocumento(id, documento, nome);
        setDocumento(resposta.documento.dados);
        setAvisos(resposta.avisos);
        mudou.current = false;
        setEstado('salvo');
        setErro('');
        setCamposDoErro([]);
      } catch (causa) {
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível salvar.');
        setCamposDoErro(camposRecusados(causa, documento));
        setEstado('pronto');
        if (!silencioso) return;
      }
    },
    [documento, id, nome],
  );

  /* Ctrl+S salva: quem escreve muito espera isso de qualquer editor. */
  useEffect(() => {
    const atalho = (evento: KeyboardEvent) => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 's') {
        evento.preventDefault();
        void salvar();
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, [salvar]);

  function imprimir() {
    setImprimindo(true);
    // Um quadro de espera para o portal montar e as imagens aparecerem.
    window.setTimeout(() => window.print(), 400);
  }

  /**
   * Baixa o documento em `.docx` — a versão editável no Word.
   *
   * O texto sai do mesmo documento que desenha o papel: títulos viram estilos de
   * título, parágrafos e listas viram parágrafos e listas, e as tabelas saem como
   * tabelas de verdade.
   */
  async function baixarDocx() {
    if (!documento) return;
    try {
      await baixarDocumentoDocx(documento, nomeDoArquivoDocx(documento, nome), urlDaImagem, paginas);
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível gerar o .docx.');
    }
  }

  /* ---------------- Ajudantes de edição ---------------- */
  /**
   * Troca o papel e **encaixa** os recuos no que o papel novo aceita.
   *
   * O A4 aceita oito degraus de recuo e o A5, quatro: o parágrafo recuado com oito
   * degraus num livro de bolso sairia com a linha espremida. Encaixar aqui é o que
   * mantém as três leituras iguais — o editor, a prévia e a impressão —, porque quem
   * aplica o teto é o motor (`degrauDeRecuo`), e não o CSS de cada tela.
   *
   * Só o degrau a mais se perde, e a pessoa está vendo a folha mudar de tamanho.
   */
  function trocarPapel(papelNovo: DocumentoAcademico['papel']) {
    editar((atual) => ({
      ...atual,
      papel: papelNovo,
      secoes: atual.secoes.map((secao) =>
        secao.blocos
          ? {
              ...secao,
              blocos: secao.blocos.map((bloco) => ({
                ...bloco,
                recuo: bloco.recuo ? degrauDeRecuo(bloco.recuo, papelNovo) || undefined : bloco.recuo,
              })),
            }
          : secao,
      ),
    }));
  }

  function trocarModelo(modelo: ModeloDeDocumento) {
    editar((atual) => ({
      ...atual,
      modelo,
      // Trocar de modelo **não apaga** o que a pessoa escreveu: só ajusta a
      // seleção de peças ao formato novo (o artigo não tem capa, por exemplo).
      itens: itensDoModelo(modelo, atual.itens),
    }));
  }

  function alternarItem(item: ElementoDocumento) {
    if (!documento) return;
    if (elementoObrigatorio(documento.modelo, item)) return;
    editar((atual) => ({
      ...atual,
      itens: itensDoModelo(
        atual.modelo,
        atual.itens.includes(item) ? atual.itens.filter((valor) => valor !== item) : [...atual.itens, item],
      ),
    }));
  }

  const permitidos = documento ? ELEMENTOS_POR_MODELO[documento.modelo] : [];
  const naoSalvo = estado === 'pronto' && mudou.current;

  /*
   * O papel e a geometria mandam no desenho e na impressão: folha, margens,
   * corpo do texto. No livro o A5 (148 × 210 mm) tem margem e corpo próprios —
   * a mesma conta que o motor usa (`geometriaDoPapel`).
   */
  const papel = documento ? papelDoDocumento(documento) : 'A4';
  /* A geometria do documento, e não só a do papel: o padrão da instituição pode
     ter margem e recuo próprios, e a prévia tem de mostrar o que vai sair. */
  const geometria = documento ? geometriaDoDocumento(documento) : geometriaDoPapel(papel);
  const podeTrocarPapel = documento ? papelEscolhivel(documento.modelo) : false;
  const ehLivro = documento?.modelo === 'LIVRO';
  /* O artigo se publica: não tem capa para encadernar nem folha de rosto. */
  const ehArtigo = documento?.modelo === 'ARTIGO_CIENTIFICO';
  /**
   * Até onde o recuo pode andar neste papel: o motor é quem sabe (o A4 aceita oito
   * degraus, o A5 quatro). A barra usa o mesmo número para parar o botão.
   */
  const maximoDeRecuo = degrausDeRecuoDoPapel(papel);

  /*
   * O CSS da folha (o `@page`, com as caixas de margem) é do motor de desenho:
   * aqui só se injeta o que ele devolve, na hora de imprimir. A margem de cima
   * vem junto porque a imagem de cabeçalho é presa dentro dela.
   */
  const folhaDeImpressao = useMemo(() => (documento ? cssDaFolha(documento) : null), [documento]);

  /*
   * A prévia é o **documento de verdade** (o mesmo componente que imprime), agora
   * só como desenho: quem escreve é o editor de cima. O que a pessoa vê aqui é o
   * que sai no papel — e o que ela lê aqui é a folha da norma, que nenhum editor de
   * texto de mercado sabe desenhar.
   */
  /** A página de cada seção, medida na prévia — é o que o sumário escreve. */
  const [paginas, setPaginas] = useState<Map<string, number>>(new Map());

  /*
   * A prévia é o **documento de verdade** (o mesmo componente que imprime), agora
   * só como desenho: quem escreve é o editor de cima. O que a pessoa vê aqui é o
   * que sai no papel — e o que ela lê aqui é a folha da norma, que nenhum editor de
   * texto de mercado sabe desenhar.
   */
  const previa = useMemo(
    () =>
      documento ? (
        <DocumentoImpresso documento={documento} resolverImagem={urlDaImagem} paginas={paginas} />
      ) : null,
    [documento, paginas],
  );

  useEffect(() => {
    if (!documento) return;
    /*
     * O sumário pede a página de cada título, e o texto **corre**: só o navegador
     * sabe onde cada seção caiu. Mede-se o desenho de verdade (a prévia) e a conta
     * volta para o motor (`paginasDoSumario`), que sabe a abertura de capítulo e as
     * folhas de anotação. O `requestAnimationFrame` garante o DOM já desenhado.
     */
    const quadro = window.requestAnimationFrame(() => {
      const raiz = document.querySelector<HTMLElement>('[data-previa] .documento-abnt');
      if (!raiz) return;
      const larguraPx = raiz.getBoundingClientRect().width;
      if (larguraPx <= 0) return;
      const geometria = geometriaDoDocumento(documento);
      const pxPorMm = larguraPx / geometria.larguraMm;
      const raizTopo = raiz.getBoundingClientRect().top;
      const secoes = raiz.querySelectorAll<HTMLElement>('[data-elemento="TEXTO"]');
      const medidas = Array.from(secoes).map((el) => ({
        id: el.id.replace(/^secao-/, ''),
        topoMm: (el.getBoundingClientRect().top - raizTopo) / pxPorMm,
      }));
      setPaginas(paginasDoSumario(documento, medidas, { geometria }));
    });
    return () => window.cancelAnimationFrame(quadro);
  }, [documento, versaoDoTexto]);

  /** O corpo do texto dentro do editor: é o que faz o editor parecer o papel. */
  const estiloDoEditor = useMemo(() => (documento ? cssDoCorpoDoEditor(documento) : ''), [documento]);

  if (estado === 'carregando') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Abrindo o documento...</span>
      </div>
    );
  }

  if (!documento) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Documento não encontrado</CardTitle>
          <CardDescription>{erro || 'Ele pode ter sido apagado.'}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="contorno">
            <Link to="/documentos">
              <ArrowLeft />
              Voltar para meus documentos
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Barra do documento */}
      <header className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <Button asChild variant="fantasma" tamanho="pequeno">
          <Link to="/documentos">
            <ArrowLeft />
            Meus documentos
          </Link>
        </Button>

        <input
          value={nome}
          onChange={(evento) => {
            setNome(evento.target.value);
            mudou.current = true;
            setEstado('pronto');
          }}
          className="h-9 min-w-[200px] flex-1 rounded-md border border-input bg-background px-3 text-sm"
          aria-label="Nome do documento"
        />

        {/*
          Dois menus em vez de uma coluna de painéis: **Formatação** (os formatos e
          estilos — modelo, papel, folha, capítulo, faixas, anotação) e **Documento**
          (o conteúdo: identificação, resumo, peças, figuras, referências, imagens).
          O que fica na tela é o papel.
        */}
        <Menu rotulo="Formatação" largura="min(94vw, 34rem)">
          {() => (
            <>
              <GrupoDoMenu titulo="Modelo do documento">
                <Select value={documento.modelo} onValueChange={(valor) => trocarModelo(valor as ModeloDeDocumento)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELOS_DE_DOCUMENTO.map((modelo) => (
                      <SelectItem key={modelo} value={modelo}>
                        {ROTULO_MODELO_DE_DOCUMENTO[modelo]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs leading-relaxed texto-suave">
                  Trocar de modelo não apaga o que está escrito: só ajusta as peças (o artigo não tem capa,
                  por exemplo).
                </p>
              </GrupoDoMenu>

              <GrupoDoMenu titulo={podeTrocarPapel ? 'Papel' : 'Papel (A4 por norma)'}>
                <Select
                  value={papel}
                  onValueChange={(valor) => trocarPapel(valor as DocumentoAcademico['papel'])}
                  disabled={!podeTrocarPapel}
                >
                  <SelectTrigger className="w-full" title={DESCRICAO_PAPEL_DE_DOCUMENTO[papel]}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAPEIS_DE_DOCUMENTO.map((item) => (
                      <SelectItem key={item} value={item} disabled={!podeTrocarPapel && item !== 'A4'}>
                        {ROTULO_PAPEL_DE_DOCUMENTO[item]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!podeTrocarPapel ? (
                  <p className="text-xs leading-relaxed texto-suave">
                    O trabalho acadêmico e o artigo saem em A4: é o papel que a norma dos
                    trabalhos acadêmicos exige. Para escolher entre A4 e A5, use o modelo <strong>Livro</strong>.
                  </p>
                ) : null}
              </GrupoDoMenu>

              {ehLivro ? (
                <GrupoDoMenu titulo="Tamanho do texto e dos títulos">
                  <div className="grid grid-cols-2 gap-3">
                    <Campo rotulo="Texto (pt)" dica={`Padrão no ${papel}: ${geometria.corpoPt} pt`}>
                      <input
                        type="number"
                        min={8}
                        max={24}
                        value={documento.corpoDoTextoPt ?? ''}
                        placeholder={String(geometria.corpoPt)}
                        onChange={(evento) => {
                          const valor = Number.parseInt(evento.target.value, 10);
                          editar((atual) => ({
                            ...atual,
                            corpoDoTextoPt:
                              Number.isFinite(valor) && valor >= 8 && valor <= 24 ? valor : undefined,
                          }));
                        }}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      />
                    </Campo>
                    <Campo
                      rotulo="Título (pt)"
                      dica={`Padrão: ${geometria.corpoPt + formatoDoCapitulo(documento).aumentoPt} pt`}
                    >
                      <input
                        type="number"
                        min={10}
                        max={40}
                        value={documento.tamanhoDoTituloPt ?? ''}
                        placeholder={String(geometria.corpoPt + formatoDoCapitulo(documento).aumentoPt)}
                        onChange={(evento) => {
                          const valor = Number.parseInt(evento.target.value, 10);
                          editar((atual) => ({
                            ...atual,
                            tamanhoDoTituloPt:
                              Number.isFinite(valor) && valor >= 10 && valor <= 40 ? valor : undefined,
                          }));
                        }}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      />
                    </Campo>
                  </div>
                  <p className="text-xs leading-relaxed texto-suave">
                    Aumente o corpo para ler melhor no A5: o texto e os títulos crescem na prévia, no PDF e na
                    exportação.
                  </p>
                </GrupoDoMenu>
              ) : null}

              <GrupoDoMenu titulo="Padrão da instituição">
                <PainelPerfil documento={documento} editar={editar} />
              </GrupoDoMenu>

              <GrupoDoMenu titulo="Fonte do documento">
                <Select
                  value={fonteDoDocumento(documento)}
                  onValueChange={(valor) =>
                    editar((atual) => ({ ...atual, fonte: valor as FonteDoDocumento }))
                  }
                >
                  <SelectTrigger className="w-full" title={DESCRICAO_FONTE_DO_DOCUMENTO[fonteDoDocumento(documento)]}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONTES_DO_DOCUMENTO.map((item) => (
                      <SelectItem key={item} value={item}>
                        {ROTULO_FONTE_DO_DOCUMENTO[item]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs leading-relaxed texto-suave">
                  {DESCRICAO_FONTE_DO_DOCUMENTO[fonteDoDocumento(documento)]} A escolha vale para o texto, para
                  o cabeçalho e o rodapé e para o editor — o que você vê é o que sai na impressão.
                </p>
              </GrupoDoMenu>

              <GrupoDoMenu titulo="Folha, capítulo e anotação">
                <PainelFolha
                  documento={documento}
                  editar={editar}
                  livro={ehLivro}
                  aoMarcarAnotacao={() => alternarItem('PAGINA_DE_ANOTACAO')}
                />
              </GrupoDoMenu>

              <GrupoDoMenu titulo="Parágrafo">
                <Select
                  value={formatoDeParagrafo(documento)}
                  onValueChange={(valor) =>
                    editar((atual) => ({ ...atual, paragrafo: valor as FormatoDeParagrafo }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMATOS_DE_PARAGRAFO.map((item) => (
                      <SelectItem key={item} value={item}>
                        {ROTULO_FORMATO_DE_PARAGRAFO[item]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs leading-relaxed texto-suave">
                  {DESCRICAO_FORMATO_DE_PARAGRAFO[formatoDeParagrafo(documento)]}
                </p>
              </GrupoDoMenu>

              {/*
                A norma da citação e da lista.
                
                Fica aqui, junto do papel e do parágrafo, porque é uma escolha de
                **forma** — e o que ela muda é só isso: a linha da lista e a citação no
                texto. O papel (margens, entrelinha, geometria da folha) continua o da
                ABNT, e a tela diz isso em vez de deixar a pessoa descobrir sozinha.
              */}
              <GrupoDoMenu titulo="Norma da citação e da lista">
                <Select
                  value={norma}
                  onValueChange={(valor) =>
                    editar((atual) => ({ ...atual, norma: valor as NormaDeCitacao }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NORMAS.map((item) => (
                      <SelectItem key={item} value={item}>
                        {ROTULO_NORMA[item]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="space-y-2 text-xs leading-relaxed texto-suave">
                  <p>{DESCRICAO_NORMA[norma]}</p>
                  <p>
                    <strong className="font-medium text-foreground">A lista sai assim:</strong>{' '}
                    {RESUMO_DA_NORMA[norma].lista} — e a citação no texto, como{' '}
                    {RESUMO_DA_NORMA[norma].citacao}.
                  </p>
                  {normaNumerada(norma) ? (
                    <p>
                      O <strong>número</strong> de cada obra é dado pela primeira vez que ela é
                      citada no texto (no botão <strong>Citar</strong>) e pode ser corrigido na
                      ficha da referência, em <strong>Número na lista</strong>.
                    </p>
                  ) : null}
                  {norma !== 'ABNT' ? (
                    <p>
                      O <strong>papel</strong> (margens e entrelinha) continua o da ABNT por
                      enquanto.
                    </p>
                  ) : null}
                </div>
              </GrupoDoMenu>

              <GrupoDoMenu titulo="Voltar ao padrão">
                <Button
                  variant="contorno"
                  tamanho="pequeno"
                  onClick={() => editar((atual) => ({ ...atual, ...formatacaoPadrao(atual.modelo) }))}
                >
                  <Sparkles />
                  Padrão ABNT
                </Button>
                <p className="text-xs leading-relaxed texto-suave">
                  Número de página, abertura de capítulo e estilo das faixas exatamente como saem num documento
                  novo — é onde voltar quando o estilo se perdeu.
                </p>
              </GrupoDoMenu>
            </>
          )}
        </Menu>

        {/*
          Não existe mais escolha de modo: a tela **é** o papel. O seletor
          Painéis/Escrever ficou sem efeito quando os painéis viraram menu, e um
          botão que não faz nada é pior que botão nenhum.
        */}
        {naoSalvo ? <Badge tom="destaque">Mudanças não salvas</Badge> : null}
        {estado === 'salvo' ? (
          <Badge tom="sucesso">
            <Check className="size-3" />
            Salvo
          </Badge>
        ) : null}

        <Button tamanho="pequeno" onClick={() => void salvar()} disabled={estado === 'salvando'}>
          {estado === 'salvando' ? <Loader2 className="animate-spin" /> : <Save />}
          Salvar
        </Button>
        <Button variant="contorno" tamanho="pequeno" onClick={imprimir}>
          <FileDown />
          Imprimir / PDF
        </Button>
        <Button variant="contorno" tamanho="pequeno" onClick={() => void baixarDocx()} disabled={!documento}>
          <FileText />
          Baixar .docx
        </Button>
      </header>

      {/*
        O papel sai pelo diálogo do navegador, e a imagem do cabeçalho é o **fundo de
        uma caixa de margem** do `@page` (ver `cssDaFolha`) — o único jeito de ela se
        repetir no alto de toda folha. Fundo só sai impresso com **Gráficos de fundo**
        marcado, e o navegador abre o diálogo com ele desmarcado: sem este aviso, a
        pessoa imprime, não vê a imagem e o recurso parece quebrado.
      */}
      {documento?.cabecalho ? (
        <p className="flex items-start gap-2 text-xs leading-relaxed texto-suave">
          <Info className="mt-0.5 size-4 shrink-0" />
          <span>
            Ao salvar em PDF, marque <strong>Gráficos de fundo</strong> no diálogo do
            navegador: é o que faz a imagem do cabeçalho, o papel decorado e a pauta das
            anotações saírem no papel.
          </span>
        </p>
      ) : null}

      {erro ? (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span>
            {erro}
            {/*
              O servidor recusou a gravação por causa de um campo, e diz **qual**:
              sem esta lista a pessoa só lia "Confira os campos enviados" e não sabia
              o que corrigir.
            */}
            {camposDoErro.length > 0 ? (
              <ul className="mt-1 space-y-0.5">
                {camposDoErro.map((campo) => (
                  <li key={campo}>· {campo}</li>
                ))}
              </ul>
            ) : null}
          </span>
        </p>
      ) : null}

      {!podeTrocarPapel ? (
        <p className="text-xs text-muted-foreground">
          O trabalho acadêmico e o artigo saem em <strong>A4</strong>: é o papel que a norma dos
          trabalhos acadêmicos exige. Para escolher entre A4 e A5, use o modelo <strong>Livro</strong>.
        </p>
      ) : null}

      {avisos.filter((aviso) => aviso.nivel === 'ATENCAO').length > 0 ? (
        <details className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            O que ainda falta para a banca ({avisos.filter((aviso) => aviso.nivel === 'ATENCAO').length})
          </summary>
          <ul className="mt-1.5 space-y-0.5 text-muted-foreground">
            {avisos
              .filter((aviso) => aviso.nivel === 'ATENCAO')
              .map((aviso) => (
                <li key={aviso.mensagem}>· {aviso.mensagem}</li>
              ))}
          </ul>
        </details>
      ) : null}

      {/*
        A conferência da norma (avisos `INFO`, do motor). Ela existe porque o aviso
        que não aparece não serve para nada: até aqui o editor mostrava só o que
        **trava** a impressão, e a conferência de citação e de folha ficava guardada
        sem ninguém ver. Fechada por padrão, e aberta sem esconder nada.
      */}
      {avisos.filter((aviso) => aviso.nivel === 'INFO').length > 0 ? (
        <details className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            Conferência da norma ({avisos.filter((aviso) => aviso.nivel === 'INFO').length})
          </summary>
          <ul className="mt-1.5 space-y-0.5 text-muted-foreground">
            {avisos
              .filter((aviso) => aviso.nivel === 'INFO')
              .map((aviso) => (
                <li key={aviso.mensagem}>· {aviso.mensagem}</li>
              ))}
          </ul>
        </details>
      ) : null}

      {/*
        A tela em três colunas, como um editor de trabalho acadêmico de verdade:
        **as partes do documento à esquerda** (é por elas que se anda no trabalho),
        **a escrita no meio** (o editor de texto, com a barra de opções acima) e
        **como sai impresso à direita** (o papel da norma, o mesmo que imprime).

        No celular as três empilham, e a **ordem é outra**: primeiro a escrita (é o
        que a pessoa veio fazer — com as partes antes, ela teria de rolar uma lista
        longa para chegar ao texto), depois o papel, e a navegação do documento no
        fim. A ordem do DOM continua a de sempre (partes, escrita, papel), que é a
        ordem de leitura de quem passa o dedo pela tela com o leitor de tela.

        Toda coluna leva `min-w-0`: sem isso, um filho largo (o papel da prévia, que
        é medido em milímetros) **estica a coluna** e a página inteira passa a rolar
        de lado — era o defeito medido a 390 px (832 px de rolagem numa janela de 390).
      */}
      <div className="grid gap-3 lg:grid-cols-[13.5rem_minmax(0,1fr)_minmax(18rem,30rem)] xl:grid-cols-[14.5rem_minmax(0,1fr)_minmax(22rem,36rem)]">
        <aside className="order-3 min-w-0 space-y-3 lg:order-none lg:sticky lg:top-3 lg:max-h-[calc(100vh-1.5rem)] lg:self-start lg:overflow-auto">
          {/*
            As partes do documento: cada uma é uma caixa que abre no lugar do texto.
            A ordem é a da norma, e a lista é **fechada** — o que o modelo não tem
            (o artigo não tem capa) também não aparece aqui.
          */}
          <nav
            aria-label="Partes do documento"
            className="rounded-lg border border-border bg-card p-2"
            data-partes-do-documento=""
          >
            <p className="px-1 pb-1.5 text-xs font-semibold uppercase tracking-wide texto-suave">
              Partes do documento
            </p>
            <ul className="space-y-0.5">
              {PARTES_DO_DOCUMENTO.map((parte) => {
                const rotulo =
                  (ehLivro && parte.rotuloLivro) || (ehArtigo && parte.rotuloArtigo) || parte.rotulo;
                const aberta = aba === parte.aba;
                return (
                  <li key={parte.aba}>
                    <button
                      type="button"
                      onClick={() => setAba(parte.aba)}
                      aria-current={aberta ? 'true' : undefined}
                      title={parte.dica}
                      className={[
                        'w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                        aberta
                          ? 'bg-primary/10 font-medium text-primary'
                          : 'hover:bg-muted',
                      ].join(' ')}
                    >
                      {rotulo}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/*
            As **peças** do modelo, uma a uma, na ordem em que a norma as apresenta.

            É a lista que responde "o que este documento pode ter, e o que eu quero
            nele": no trabalho acadêmico são as peças da ABNT (capa, folha de rosto,
            folha de aprovação, resumo, abstract, listas, sumário, referências...); no
            livro, as dele — capa, sumário (o índice), capítulos, sobre o autor (a
            contracapa). Clicar numa peça abre o painel onde ela se escreve; a caixinha
            à esquerda liga e desliga a peça no documento.

            O que não é do modelo **não aparece**: a lista é fechada, e é isso que faz
            o artigo não mostrar capa e o livro não mostrar resumo.
          */}
          <nav
            aria-label="Peças do documento"
            className="rounded-lg border border-border bg-card p-2"
            data-pecas-do-documento=""
          >
            <p className="px-1 pb-1.5 text-xs font-semibold uppercase tracking-wide texto-suave">
              Peças do documento
            </p>
            <div className="space-y-2">
              {GRUPOS_DOCUMENTO.map((grupo) => {
                const pecas = pecasDoGrupo(documento.modelo, permitidos, grupo);
                if (pecas.length === 0) return null;
                return (
                  <section key={grupo} className="space-y-0.5">
                    <p
                      className="px-1 text-[11px] font-medium uppercase tracking-wide texto-suave"
                      title={DESCRICAO_GRUPO_DOCUMENTO[grupo]}
                    >
                      {ROTULO_GRUPO_DOCUMENTO[grupo]}
                    </p>
                    <ul>
                      {pecas.map((item) => {
                        const obrigatoria = elementoObrigatorio(documento.modelo, item);
                        const marcada = documento.itens.includes(item) || obrigatoria;
                        const destino = PARTE_DA_PECA[item];
                        const rotulo =
                          (ehLivro && ROTULO_DA_PECA_LIVRO[item]) || ROTULO_ELEMENTO[item];
                        return (
                          <li key={item} className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              checked={marcada}
                              disabled={obrigatoria}
                              onChange={() => alternarItem(item)}
                              aria-label={`${ROTULO_ELEMENTO[item]} no documento`}
                              title={
                                obrigatoria
                                  ? `${ROTULO_ELEMENTO[item]}: obrigatória neste modelo, não sai do documento`
                                  : `Ligar ou desligar ${ROTULO_ELEMENTO[item].toLowerCase()} no documento`
                              }
                              className="size-3.5 shrink-0 accent-primary"
                            />
                            {destino ? (
                              <button
                                type="button"
                                onClick={() => setAba(destino)}
                                title={`${DESCRICAO_ELEMENTO[item]} — abre onde ela se escreve`}
                                className="w-full rounded px-1.5 py-1 text-left text-sm leading-snug transition-colors hover:bg-muted"
                              >
                                {rotulo}
                              </button>
                            ) : (
                              <span
                                className="w-full px-1.5 py-1 text-sm leading-snug texto-suave"
                                title={`${DESCRICAO_ELEMENTO[item]} — o motor monta esta peça; aqui só se liga e desliga`}
                              >
                                {rotulo}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          </nav>

          {/*
            O sumário, montado do documento (as mesmas seções, na mesma ordem e com a
            mesma numeração que sai impressa). Clicar leva o cursor para o título
            daquela seção dentro do editor — é o jeito de andar num texto longo sem
            ficar rolando.
          */}
          <nav
            aria-label="Sumário"
            className="rounded-lg border border-border bg-card p-2"
            data-sumario=""
          >
            <p className="px-1 pb-1.5 text-xs font-semibold uppercase tracking-wide texto-suave">
              Sumário
            </p>
            {documento.secoes.length === 0 ? (
              <p className="px-2 py-1 text-xs texto-suave">
                O trabalho ainda não tem seções.
              </p>
            ) : (
              <ol className="space-y-0.5">
                {numerarSecoes(documento.secoes, ehLivro ? { livro: true } : {}).map(
                  (secao) => (
                    <li key={secao.secao.id}>
                      <button
                        type="button"
                        onClick={() => irParaASecao(secao.secao)}
                        title="Ir para esta seção no texto"
                        className="w-full rounded px-2 py-1 text-left text-xs leading-relaxed transition-colors hover:bg-muted"
                        style={{ paddingLeft: `${8 + (secao.nivel - 1) * 10}px` }}
                      >
                        {secao.numero ? (
                          <span className="texto-suave">{secao.numero} </span>
                        ) : null}
                        {/*
                          O título sai como a folha o escreve (`secao.titulo`): a caixa
                          alta do título primário é regra do modelo, e o sumário da
                          esquerda é o caminho para andar no texto — se ele mostrasse
                          outra caixa, pareceria que a regra não foi aplicada. As marcas
                          do Markdown vêm por `comMarcas`, para o negrito do título não
                          sair como asteriscos.
                        */}
                        {comMarcas(secao.titulo.trim() || 'Sem título')}
                      </button>
                    </li>
                  ),
                )}
              </ol>
            )}
          </nav>
        </aside>

        {/* A escrita: as opções em cima (na barra) e a área de digitação abaixo. */}
        <div className="order-1 min-w-0 space-y-3 lg:order-none">
          {aba === 'TEXTO' ? (
            <>
              <BarraDeEscrita
                livro={ehLivro}
                norma={norma}
                nivelDoCursor={nivelDoCursor}
                recuoDoCursor={recuoDoCursor}
                maximoDeRecuo={maximoDeRecuo}
                /*
                 * A lista crua, na ordem em que as referências estão no documento: quem
                 * ordena é `listaDaNorma` — alfabética nas normas autor-data, e a ordem do
                 * cadastro para o **número provisório** das numeradas (a mesma que o gesto
                 * de citar usa, senão o menu mostraria um número e gravaria outro).
                 */
                referencias={documento?.referencias ?? []}
                salvando={estado === 'salvando'}
                naoSalvo={naoSalvo}
                aoMudarNivel={(nivel) => editorDeTexto.current?.nivelDaSecao(nivel)}
                aoNovaSecao={() => editorDeTexto.current?.novaSecao()}
                aoCitacaoLonga={() => editorDeTexto.current?.transformarEmCitacao()}
                aoAspas={() => editorDeTexto.current?.darAspas()}
                aoInserirNota={() => editorDeTexto.current?.inserirNota(proximoNumeroDeNota)}
                aoInserirImagem={(arquivo) => void enviarImagemParaOTexto(arquivo)}
                proximoNumeroDeNota={proximoNumeroDeNota}
                aoMudarRecuo={(passo) => editorDeTexto.current?.recuoDoBloco(passo)}
                aoCitar={citar}
                aoSalvar={() => void salvar()}
              />

              {/*
                O editor: é onde se escreve, com negrito, itálico, lista, citação e
                tabela. A folha da norma não se reproduz dentro dele (nem é o caso) —
                ela está ali na direita, montada pelo mesmo desenho que imprime.
              */}
              <EditorDeTexto
                ref={editorDeTexto}
                html={htmlDoDocumento}
                versao={versaoDoTexto}
                aoMudar={aoMudarHtml}
                aoTrocarBloco={(estado) => {
                  setNivelDoCursor(estado.nivel);
                  setRecuoDoCursor(estado.recuo);
                }}
                aoSalvar={() => void salvar()}
                estiloDoCorpo={estiloDoEditor}
                altura={alturaDoEditor}
                maximoDeRecuo={maximoDeRecuo}
              />

              {/*
                As duas portas que o **editor** não tem, recolhidas — quem só escreve
                nunca precisa abrir:

                1. **trazer um texto de fora** (do Word, do Markdown, de outro editor):
                   a leitura reconhece os títulos, o resumo, as palavras-chave, as
                   citações e as referências, e o `.md` é também o caminho de saída;
                2. **legenda e fonte das tabelas**, que o editor não escreve (o papel
                   numera a tabela em cima e escreve a fonte embaixo).

                O resto da arrumação — texto, lista, citação, tabela, o nível da seção —
                se faz no próprio editor, que é onde se escreve.
              */}
              <details
                className="rounded-lg border border-border bg-card p-3"
                data-traz-texto-de-fora=""
              >
                <summary className="cursor-pointer text-sm font-medium">
                  Trazer um texto de fora (.md ou colado)
                </summary>
                <div className="pt-3">
                  <CartaoColarTexto documento={documento} editar={editar} livro={ehLivro} />
                </div>
              </details>

              <CartaoDeTabelas documento={documento} editar={editar} />
              <CartaoDeFiguras documento={documento} editar={editar} />
              <CartaoDeNotas documento={documento} editar={editar} />
            </>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>
                  {ehLivro && aba === 'RESUMO'
                    ? 'Abertura e fecho'
                    : ehLivro && aba === 'FOLHA'
                      ? 'Folha e capítulos'
                      : ROTULO_ABA[aba]}
                </CardTitle>
                <CardDescription>
                  {PARTES_DO_DOCUMENTO.find((parte) => parte.aba === aba)?.dica}
                  {' '}
                  Para voltar a escrever, escolha <strong>O texto</strong> na coluna da esquerda.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {aba === 'IDENTIFICACAO' ? (
                  <PainelIdentificacao documento={documento} editar={editar} />
                ) : aba === 'RESUMO' ? (
                  <PainelResumo documento={documento} editar={editar} livro={ehLivro} />
                ) : aba === 'ITENS' ? (
                  <PainelItens
                    modelo={documento.modelo}
                    selecionados={documento.itens}
                    permitidos={permitidos}
                    aoAlternar={alternarItem}
                  />
                ) : aba === 'FOLHA' ? (
                  <PainelFolha
                    documento={documento}
                    editar={editar}
                    livro={ehLivro}
                    aoMarcarAnotacao={() => alternarItem('PAGINA_DE_ANOTACAO')}
                  />
                ) : aba === 'FIGURAS' ? (
                  <PainelFiguras documento={documento} editar={editar} />
                ) : aba === 'REFERENCIAS' ? (
                  <PainelReferencias documento={documento} editar={editar} />
                ) : aba === 'ANEXOS' ? (
                  <PainelAnexos documento={documento} editar={editar} />
                ) : (
                  <PainelImagens documento={documento} editar={editar} />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Como sai impresso: o papel da norma, o mesmo desenho que a impressão usa. */}
        <aside className="order-2 min-w-0 space-y-2 lg:order-none">
          <p className="text-xs font-medium texto-suave">Como sai impresso</p>
          <div
            className="rounded-lg border border-border bg-muted/40 p-3 lg:sticky lg:top-3 lg:max-h-[calc(100vh-3rem)] lg:overflow-auto"
            data-previa=""
          >
            {/*
              A folha é medida em milímetros: o A4 tem **794 px** de largura e não
              encolhe sozinho. Um `transform: scale()` só a encolhe no desenho — a
              caixa continua com 794 px, e é ela que esticava a coluna e fazia a página
              rolar de lado no celular (o defeito medido: 832 px numa janela de 390).
              Pior: com `scale` a caixa ficava alta do tamanho natural e sobrava um
              vazio embaixo do desenho.

              Aqui o encolhimento é por `zoom`, que **muda a medida da caixa**: o papel
              cabe na coluna, a página não rola de lado e não sobra vazio. O fator anda
              com a largura da **coluna**, não com a da janela — por isso ele diminui no
              `lg`, quando a tela ganha três colunas e a prévia passa a dividir o espaço.
            */}
            <div className="mx-auto w-fit origin-top zoom-[0.3] min-[360px]:zoom-[0.42] sm:zoom-[0.72] lg:zoom-[0.56] xl:zoom-[0.7]">
              {previa}
            </div>
          </div>
        </aside>
      </div>

      {/*
        Portal de impressão: só existe na hora de imprimir, e vai **para o
        `body`** (`createPortal`).

        Não é detalhe de estilo: em mídia de impressão o site esconde tudo que não
        seja filho direto do `body` (`.area-impressao` é a única exceção). Montada
        dentro da árvore do editor, a área de impressão era escondida junto com o
        resto da tela — e o documento saía numa folha em branco, sem uma linha de
        texto (só a faixa do `@page`, que vale mesmo dentro de um elemento
        escondido). Foi o teste da folha que pegou isso, lendo o PDF de volta.
      */}
      {imprimindo && folhaDeImpressao
        ? createPortal(
            <div className="area-impressao">
              {/*
                As margens da norma viram margens **de página** (`@page`), e não
                `padding` do bloco: padding só vale na primeira e na última folha,
                e o texto encostaria no papel do meio do trabalho em diante. Com a
                margem na página, toda folha sai igual.

                O `@page` vem do desenho da folha (`cssDaFolha`), que também
                escreve as caixas de cabeçalho, rodapé e número — e diz a margem de
                cima, que a imagem de cabeçalho usa para não cobrir uma linha de
                texto.
              */}
              <style>{`${folhaDeImpressao.pagina}
                .area-impressao .documento-abnt { padding: 0 !important; width: auto !important; }`}</style>
              <DocumentoImpresso documento={documento} resolverImagem={urlDaImagem} paginas={paginas} />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Painéis                                                             */
/* ------------------------------------------------------------------ */

type Editar = (mudanca: (atual: DocumentoAcademico) => DocumentoAcademico) => void;

/** Um campo que o servidor recusou, como o servidor o devolveu. */
interface CampoRecusado {
  campo: string;
  mensagem: string;
}

/**
 * O nome do campo como a pessoa o conhece — "documento.secoes.0.titulo" vira
 * "Título da seção “Apresentação”".
 *
 * O servidor manda o caminho do campo (e o motivo, em português): aqui o caminho
 * ganha o nome que está na tela. Campo sem tradução sai como veio — é melhor mostrar
 * um caminho técnico do que esconder o que a gravação recusou.
 */
function rotuloDoCampo(campo: string, documento: DocumentoAcademico | null): string {
  if (campo === 'nome') return 'Nome do documento';

  /*
   * O limite do documento **inteiro** cai aqui sem índice nenhum: o caminho é
   * `documento.secoes`, e não `documento.secoes.3.titulo`. Sem este caso, o índice
   * virava `NaN` e o aviso dizia "A seção nº NaN: passa do limite de 200" — que não
   * diz nem o que passou do limite nem quantas seções o livro tem. É o aviso de um
   * livro grande demais (cada lição colada entra como capítulo e as partes como
   * subseções), e quem escreve precisa saber a conta para poder agir.
   */
  if (campo === 'documento.secoes') {
    const quantas = documento?.secoes.length ?? 0;
    return quantas > 0
      ? `As seções do documento (ele está com ${quantas})`
      : 'As seções do documento';
  }

  const partes = campo.split('.');
  /* Seção é `documento.secoes.<índice>…`: sem o número, não é uma seção. */
  const ehSecao =
    partes[0] === 'documento' && partes[1] === 'secoes' && /^\d+$/.test(partes[2] ?? '');
  if (ehSecao) {
    const indice = Number(partes[2]);
    const titulo = documento?.secoes[indice]?.titulo?.trim();
    const onde = titulo ? `“${titulo.slice(0, 40)}${titulo.length > 40 ? '…' : ''}”` : `nº ${indice + 1}`;
    const qual = partes[3] ?? '';
    if (qual === 'titulo') return `Título da seção ${onde}`;
    if (qual === 'blocos') {
      const bloco = partes[5] ?? '';
      if (bloco === 'texto' || bloco === 'itens') return `Texto de um bloco da seção ${onde}`;
      if (bloco === 'linhas') return `Tabela de um bloco da seção ${onde}`;
      return `Um bloco da seção ${onde}`;
    }
    if (qual === 'paragrafos' || qual === 'citacaoLonga') return `Texto da seção ${onde}`;
    return `A seção ${onde}`;
  }

  const nomes: Record<string, string> = {
    'documento.identificacao.titulo': 'Título do documento',
    'documento.identificacao.subtitulo': 'Subtítulo do documento',
    'documento.identificacao.ano': 'Ano',
    'documento.resumo': 'Resumo',
    'documento.abstract': 'Abstract',
    'documento.palavrasChave': 'Palavras-chave',
    'documento.dedicatoria': 'Dedicatória',
    'documento.agradecimentos': 'Agradecimentos',
    'documento.epigrafe': 'Epígrafe',
    'documento.apresentacao': 'Apresentação',
    'documento.epilogo': 'Epílogo',
    'documento.sobreOAutor': 'Sobre o autor',
    'documento.folha': 'Folha (cabeçalho, rodapé, número)',
    'documento.capitulo': 'Como o capítulo abre',
    'documento.modelo': 'Modelo do documento',
  };
  return nomes[campo] ?? campo;
}

/**
 * O que a gravação recusou, linha por linha.
 *
 * A mensagem do servidor ('Confira os campos enviados.') não diz **qual** campo — e
 * era o que fazia a pessoa não saber o que corrigir. O detalhe vem no corpo do erro
 * (`detalhes`), e é ele que vem para a tela.
 */
function camposRecusados(causa: unknown, documento: DocumentoAcademico | null): string[] {
  if (!(causa instanceof ErroApi)) return [];
  const corpo = causa.detalhes as { detalhes?: CampoRecusado[] } | undefined;
  const lista = corpo?.detalhes;
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((item) => item && typeof item.campo === 'string')
    .map((item) => `${rotuloDoCampo(item.campo, documento)}: ${item.mensagem}.`);
}

function PainelItens({
  modelo,
  selecionados,
  permitidos,
  aoAlternar,
}: {
  modelo: ModeloDeDocumento;
  selecionados: ElementoDocumento[];
  permitidos: ElementoDocumento[];
  aoAlternar: (item: ElementoDocumento) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quais peças o trabalho tem</CardTitle>
        <CardDescription>
          A norma exige algumas peças e deixa outras ao seu critério: capa, folha de rosto e referências
          não podem sair. Listas só fazem sentido quando existe o que listar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {GRUPOS_DOCUMENTO.map((grupo) => {
          /* A mesma regra da coluna da esquerda (`pecasDoGrupo`): uma lista só. */
          const itens = pecasDoGrupo(modelo, permitidos, grupo);
          if (itens.length === 0) return null;

          return (
            <section key={grupo} className="space-y-2">
              <div>
                <h3 className="text-sm font-semibold">{ROTULO_GRUPO_DOCUMENTO[grupo]}</h3>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {DESCRICAO_GRUPO_DOCUMENTO[grupo]}
                </p>
              </div>
              <Grade colunas={2}>
                {itens.map((item) => {
                  const obrigatorio = elementoObrigatorio(modelo, item);
                  const marcado = selecionados.includes(item) || obrigatorio;
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => aoAlternar(item)}
                      aria-pressed={marcado}
                      disabled={obrigatorio}
                      className={[
                        'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors',
                        marcado ? 'border-primary bg-primary/[0.04]' : 'border-border bg-card hover:border-primary/40',
                        obrigatorio ? 'cursor-default opacity-90' : '',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold',
                          marcado ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                        ].join(' ')}
                      >
                        {marcado ? '✓' : ''}
                      </span>
                      <span className="space-y-0.5">
                        <span className="block text-sm font-medium leading-tight">
                          {ROTULO_ELEMENTO[item]}
                          {obrigatorio ? ' · obrigatória' : ''}
                        </span>
                        <span className="block text-xs leading-relaxed text-muted-foreground">
                          {DESCRICAO_ELEMENTO[item]}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </Grade>
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}

function PainelIdentificacao({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const { identificacao } = documento;

  const mudar = (parcial: Partial<DocumentoAcademico['identificacao']>) =>
    editar((atual) => ({ ...atual, identificacao: { ...atual.identificacao, ...parcial } }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quem escreve, onde e sobre o quê</CardTitle>
        <CardDescription>
          É o que vai para a capa, para a folha de rosto e para as referências. O nome do autor sai como
          você digitar ("Maria Silva") e o motor escreve "SILVA, Maria" na hora de citar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Campo rotulo="Título do trabalho">
          <input
            value={identificacao.titulo}
            onChange={(evento) => mudar({ titulo: evento.target.value })}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
          />
        </Campo>

        <Campo rotulo="Subtítulo" dica="Depois dos dois-pontos do título.">
          <input
            value={identificacao.subtitulo ?? ''}
            onChange={(evento) => mudar({ subtitulo: evento.target.value })}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
          />
        </Campo>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Autores</span>
            <Button
              variant="contorno"
              tamanho="pequeno"
              onClick={() => mudar({ autores: [...identificacao.autores, { nome: '' }] })}
            >
              <Plus />
              Autor
            </Button>
          </div>
          {identificacao.autores.map((autor, indice) => (
            <div key={indice} className="flex gap-2">
              <input
                value={autor.nome}
                onChange={(evento) => {
                  const autores = [...identificacao.autores];
                  autores[indice] = { ...autor, nome: evento.target.value };
                  mudar({ autores });
                }}
                placeholder="Maria Silva"
                className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
              <input
                value={autor.instituicao ?? ''}
                onChange={(evento) => {
                  const autores = [...identificacao.autores];
                  autores[indice] = { ...autor, instituicao: evento.target.value };
                  mudar({ autores });
                }}
                placeholder="Instituição (artigo)"
                className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
              {identificacao.autores.length > 1 ? (
                <Button
                  variant="fantasma"
                  tamanho="pequeno"
                  onClick={() => mudar({ autores: identificacao.autores.filter((_, i) => i !== indice) })}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </div>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Instituição">
            <input
              value={identificacao.instituicao}
              onChange={(evento) => mudar({ instituicao: evento.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
            />
          </Campo>
          <Campo rotulo="Faculdade / unidade">
            <input
              value={identificacao.unidade ?? ''}
              onChange={(evento) => mudar({ unidade: evento.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
            />
          </Campo>
          <Campo rotulo="Curso">
            <input
              value={identificacao.curso ?? ''}
              onChange={(evento) => mudar({ curso: evento.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
            />
          </Campo>
          <Campo rotulo="Tipo de trabalho">
            <Select
              value={identificacao.tipo ?? 'TCC'}
              onValueChange={(valor) => mudar({ tipo: valor as DocumentoAcademico['identificacao']['tipo'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_DE_TRABALHO.map((tipo) => (
                  <SelectItem key={tipo} value={tipo}>
                    {ROTULO_TIPO_DE_TRABALHO[tipo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
          <Campo rotulo="Cidade">
            <input
              value={identificacao.cidade}
              onChange={(evento) => mudar({ cidade: evento.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
            />
          </Campo>
          <Campo rotulo="Ano">
            <input
              value={identificacao.ano}
              onChange={(evento) => mudar({ ano: evento.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
            />
          </Campo>
          <Campo rotulo="Orientador">
            <input
              value={identificacao.orientador ?? ''}
              onChange={(evento) => mudar({ orientador: evento.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
            />
          </Campo>
        </div>

        <Campo rotulo="Banca (folha de aprovação)" dica="Um examinador por linha.">
          <textarea
            value={identificacao.banca ?? ''}
            onChange={(evento) => mudar({ banca: evento.target.value })}
            rows={3}
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
        </Campo>
      </CardContent>
    </Card>
  );
}

function PainelResumo({
  documento,
  editar,
  livro,
}: {
  documento: DocumentoAcademico;
  editar: Editar;
  /** No livro esta aba guarda a abertura e o fecho, e não resumo/abstract. */
  livro: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{livro ? 'Abertura e fecho do livro' : 'Resumo, palavras-chave e abstract'}</CardTitle>
        <CardDescription>
          {livro
            ? 'O texto que abre o livro (apresentação, dedicatória, epígrafe), o que fecha (epílogo) e a página sobre quem escreveu. Tudo opcional: o que estiver em branco não é impresso.'
            : 'A norma quer o resumo em um parágrafo e as palavras-chave separadas por ponto e vírgula. Escreva as palavras separadas por vírgula: o motor ajusta a pontuação na saída.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {livro ? (
          <>
            <Campo rotulo="Apresentação" dica="O texto de abertura, antes do primeiro capítulo.">
              <textarea
                value={documento.apresentacao ?? ''}
                onChange={(evento) => editar((atual) => ({ ...atual, apresentacao: evento.target.value }))}
                rows={5}
                className="w-full rounded-md border border-input bg-background p-3 text-sm"
              />
            </Campo>
            <Campo rotulo="Epílogo" dica="O fecho: posfácio, últimas palavras.">
              <textarea
                value={documento.epilogo ?? ''}
                onChange={(evento) => editar((atual) => ({ ...atual, epilogo: evento.target.value }))}
                rows={5}
                className="w-full rounded-md border border-input bg-background p-3 text-sm"
              />
            </Campo>
            <Campo rotulo="Sobre o autor" dica="Uma página no fim, como a contracapa de um livro.">
              <textarea
                value={documento.sobreOAutor ?? ''}
                onChange={(evento) => editar((atual) => ({ ...atual, sobreOAutor: evento.target.value }))}
                rows={4}
                className="w-full rounded-md border border-input bg-background p-3 text-sm"
              />
            </Campo>
          </>
        ) : null}

        {livro ? null : (
          <>
        <Campo rotulo="Resumo">
          <textarea
            value={documento.resumo}
            aria-label="Resumo"
            onChange={(evento) => editar((atual) => ({ ...atual, resumo: evento.target.value }))}
            rows={5}
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
        </Campo>
        <Campo rotulo="Palavras-chave" dica="Separe por vírgula.">
          <input
            value={documento.palavrasChave.join(', ')}
            onChange={(evento) =>
              editar((atual) => ({
                ...atual,
                palavrasChave: evento.target.value.split(',').map((palavra) => palavra.trim()).filter(Boolean),
              }))
            }
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
          />
        </Campo>
        <Campo rotulo="Abstract">
          <textarea
            value={documento.abstract}
            onChange={(evento) => editar((atual) => ({ ...atual, abstract: evento.target.value }))}
            rows={5}
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
        </Campo>
        <Campo rotulo="Keywords" dica="Separe por vírgula.">
          <input
            value={documento.keywords.join(', ')}
            onChange={(evento) =>
              editar((atual) => ({
                ...atual,
                keywords: evento.target.value.split(',').map((palavra) => palavra.trim()).filter(Boolean),
              }))
            }
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
          />
        </Campo>
        <TradutorIa documento={documento} aoEditar={editar} />

        <div className="space-y-2">
          <p className="text-sm font-medium">Resumos em outros idiomas</p>
          <p className="text-xs leading-relaxed texto-suave">
            Espanhol, francês, alemão... Cada um sai em folha própria, com o rótulo e as
            palavras-chave que você definir.
          </p>
          {(documento.resumos ?? []).map((item, indice) => (
            <div key={indice} className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center gap-2">
                <input
                  value={item.titulo}
                  placeholder="Rótulo (ex.: Resumen)"
                  aria-label={`Rótulo do resumo ${indice + 1}`}
                  onChange={(evento) =>
                    editar((atual) => {
                      const resumos = [...(atual.resumos ?? [])];
                      resumos[indice] = { ...resumos[indice]!, titulo: evento.target.value };
                      return { ...atual, resumos };
                    })
                  }
                  className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                />
                <Button
                  variant="contorno"
                  tamanho="pequeno"
                  onClick={() =>
                    editar((atual) => ({
                      ...atual,
                      resumos: (atual.resumos ?? []).filter((_, i) => i !== indice),
                    }))
                  }
                >
                  <Trash2 />
                  Remover
                </Button>
              </div>
              <textarea
                value={item.resumo}
                placeholder="Texto do resumo"
                rows={4}
                onChange={(evento) =>
                  editar((atual) => {
                    const resumos = [...(atual.resumos ?? [])];
                    resumos[indice] = { ...resumos[indice]!, resumo: evento.target.value };
                    return { ...atual, resumos };
                  })
                }
                className="w-full rounded-md border border-input bg-background p-3 text-sm"
              />
              <input
                value={item.palavrasChave.join(', ')}
                placeholder="Palavras-chave, separadas por vírgula"
                onChange={(evento) =>
                  editar((atual) => {
                    const palavrasChave = evento.target.value
                      .split(',')
                      .map((p) => p.trim())
                      .filter(Boolean);
                    const resumos = [...(atual.resumos ?? [])];
                    resumos[indice] = { ...resumos[indice]!, palavrasChave };
                    return { ...atual, resumos };
                  })
                }
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>
          ))}
          <Button
            variant="contorno"
            tamanho="pequeno"
            onClick={() =>
              editar((atual) => ({
                ...atual,
                resumos: [...(atual.resumos ?? []), { titulo: '', resumo: '', palavrasChave: [] }],
              }))
            }
          >
            <Plus />
            Adicionar resumo em outro idioma
          </Button>
        </div>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Dedicatória">
            <textarea
              value={documento.dedicatoria ?? ''}
              onChange={(evento) => editar((atual) => ({ ...atual, dedicatoria: evento.target.value }))}
              rows={3}
              className="w-full rounded-md border border-input bg-background p-3 text-sm"
            />
          </Campo>
          <Campo rotulo="Epígrafe" dica="A citação que abre o trabalho, com a autoria.">
            <textarea
              value={documento.epigrafe ?? ''}
              onChange={(evento) => editar((atual) => ({ ...atual, epigrafe: evento.target.value }))}
              rows={3}
              className="w-full rounded-md border border-input bg-background p-3 text-sm"
            />
          </Campo>
        </div>

        <Campo rotulo="Agradecimentos">
          <textarea
            value={documento.agradecimentos ?? ''}
            onChange={(evento) => editar((atual) => ({ ...atual, agradecimentos: evento.target.value }))}
            rows={4}
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
        </Campo>

        <Campo rotulo="Lista de abreviaturas" dica="Uma por linha: “ABNT — Associação Brasileira de Normas Técnicas”.">
          <textarea
            value={documento.abreviaturas.join('\n')}
            onChange={(evento) =>
              editar((atual) => ({
                ...atual,
                abreviaturas: evento.target.value.split('\n').map((linha) => linha.trim()).filter(Boolean),
              }))
            }
            rows={4}
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
        </Campo>
      </CardContent>
    </Card>
  );
}

/**
 * Colar o texto pronto.
 *
 * Quem escreveu no Word chega com o trabalho inteiro na área de transferência.
 * A leitura reconhece os títulos com os níveis, o resumo, as palavras-chave, as
 * citações longas e a lista de referências; aqui a pessoa **vê o que foi
 * entendido** antes de mandar montar. Essa conferência é o coração do cartão:
 * uma leitura errada passaria despercebida e só apareceria no PDF.
 */
function CartaoColarTexto({
  documento,
  editar,
  livro,
}: {
  documento: DocumentoAcademico;
  editar: Editar;
  livro: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [substituir, setSubstituir] = useState(true);
  const [arquivo, setArquivo] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  /*
   * O Markdown é montado **na hora do clique**, e não a cada tecla: o documento
   * pode ser longo, e o `useMemo` aqui rodaria a serialização inteira a cada
   * letra digitada no editor. O que a tela precisa saber antes disso é só se há
   * texto para levar embora.
   */
  const temTexto = Boolean(
    documento.resumo.trim() ||
      documento.abstract.trim() ||
      documento.referencias.length > 0 ||
      documento.secoes.some(
        (secao) => secao.titulo.trim() || secao.paragrafos.some(Boolean) || secao.citacaoLonga?.trim(),
      ),
  );

  const lido = useMemo<TextoEstruturado | null>(
    () => (texto.trim() ? lerTextoColado(texto, documento.modelo) : null),
    [texto, documento.modelo],
  );

  const lerArquivo = async (escolhido: File | null) => {
    if (!escolhido) return;
    setArquivo(escolhido.name);
    setTexto(await escolhido.text());
  };

  const montar = () => {
    if (!lido) return;
    editar((atual) => aplicarTextoEstruturado(atual, lido, { substituir }));
    setTexto('');
    setArquivo(null);
  };

  /*
   * Copiar e baixar levam o **mesmo** texto: o `.md` do documento. Quem copia
   * cola num e-mail para o orientador; quem baixa guarda o arquivo para continuar
   * depois — e as duas coisas voltam inteiras, pelo mesmo caminho de leitura.
   */
  const copiar = async () => {
    await navigator.clipboard.writeText(documentoEmMarkdown(documento));
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  const baixar = () => {
    const markdown = documentoEmMarkdown(documento);
    const nome =
      (documento.identificacao.titulo || 'documento')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60) || 'documento';
    const endereco = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = endereco;
    link.download = `${nome}.md`;
    link.click();
    URL.revokeObjectURL(endereco);
  };

  /** "3 seções" / "1 seção": a contagem com o nome certo. */
  const contar = (valor: number, singular: string, plural: string) =>
    `${valor} ${valor === 1 ? singular : plural}`;

  const etiquetas: string[] = lido
    ? [
        contar(lido.contagem.secoes, livro ? 'capítulo' : 'seção', livro ? 'capítulos' : 'seções'),
        contar(lido.contagem.paragrafos, 'parágrafo', 'parágrafos'),
        contar(lido.contagem.citacoesLongas, 'citação longa', 'citações longas'),
        contar(lido.contagem.referencias, 'referência', 'referências'),
        contar(lido.contagem.palavras, 'palavra', 'palavras'),
      ]
    : [];

  return (
    <Card className="painel-tom" style={{ ['--tom' as string]: 'var(--cor-alegre-2)' }}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 texto-suave">
          <ClipboardPaste className="size-4" />
          Trazer um texto de fora (.md ou colado)
        </CardTitle>
        <CardDescription className="texto-suave">
          Esta caixa é a <strong>porta de entrada</strong> de um texto que já existe: o{' '}
          {livro ? 'livro' : 'trabalho'} escrito no Word, no Google Docs, num arquivo
          <code className="mx-1">.md</code> ou <code className="mx-1">.txt</code>. Cole na caixa ou escolha o
          arquivo, e o gerador reconhece os títulos (<code># Título</code>, <code>## Seção</code>), a citação
          longa (<code>&gt; citação</code>), o resumo, as palavras-chave, as listas e as referências — uma linha
          em branco vira um <strong>parágrafo</strong>, negrito se escreve <code>**assim**</code> e itálico{' '}
          <code>*assim*</code> — e monta as {livro ? 'partes' : 'seções'} de uma vez.
          {' '}
          <strong>Quem escreve o {livro ? 'capítulo' : 'texto'} é o editor</strong>, logo acima: aqui é só para
          trazer o que já estava escrito em outro lugar. O caminho de volta (copiar ou baixar o
          <code className="mx-1">.md</code>) fica no fim desta mesma caixa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <textarea
          value={texto}
          onChange={(evento) => {
            setTexto(evento.target.value);
            setArquivo(null);
          }}
          rows={8}
          aria-label="Texto colado"
          placeholder={
            '# O mar subiu naquela noite\n\nO texto do capítulo vem aqui, com **negrito** onde precisar.\n\n## Metodologia\n\n> Citação longa é assim: o `>` no começo da linha.'
          }
          className="w-full rounded-md border border-input bg-background p-3 font-mono text-sm"
        />

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
            <Upload className="size-4" />
            Escolher um arquivo .txt ou .md
            <input
              type="file"
              accept=".txt,.md,.markdown,text/plain,text/markdown"
              className="hidden"
              onChange={(evento) => void lerArquivo(evento.target.files?.[0] ?? null)}
            />
          </label>
          {arquivo ? <span className="text-xs texto-suave">Lido de {arquivo}</span> : null}
        </div>

        {lido ? (
          <div className="space-y-3 rounded-lg border border-border bg-background/70 p-3">
            <p className="text-sm font-medium">Entendi isto no seu texto:</p>
            <div className="flex flex-wrap gap-2">
              {etiquetas.map((etiqueta) => (
                <Badge key={etiqueta} tom="principal">
                  {etiqueta}
                </Badge>
              ))}
              {lido.resumo ? <Badge tom="sucesso">resumo</Badge> : null}
              {lido.palavrasChave.length > 0 ? <Badge tom="sucesso">palavras-chave</Badge> : null}
              {lido.abstract ? <Badge tom="sucesso">abstract</Badge> : null}
              {Object.entries(lido.campos).map(([campo]) => (
                <Badge key={campo} tom="sucesso">
                  {ROTULO_CAMPO_LIDO[campo as keyof typeof ROTULO_CAMPO_LIDO] ?? campo}
                </Badge>
              ))}
            </div>

            {lido.secoes.length > 0 ? (
              <ol
                data-lista-lida=""
                className="space-y-0.5 text-sm texto-suave"
              >
                {lido.secoes.slice(0, 12).map((secao) => (
                  <li key={secao.id} style={{ paddingLeft: `${(secao.nivel - 1) * 16}px` }}>
                    {secao.nivel === 1 ? '●' : '○'} {secao.titulo}{' '}
                    <span className="text-xs">({secao.paragrafos.length} parágrafos)</span>
                  </li>
                ))}
                {lido.secoes.length > 12 ? (
                  <li className="text-xs">... e mais {lido.secoes.length - 12} seções</li>
                ) : null}
              </ol>
            ) : null}

            {lido.avisos.length > 0 ? (
              <ul className="space-y-1 text-xs texto-suave">
                {lido.avisos.map((aviso) => (
                  <li key={aviso} className="flex gap-2">
                    <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                    {aviso}
                  </li>
                ))}
              </ul>
            ) : null}

            <label className="flex items-center gap-2 text-xs texto-suave">
              <input
                type="checkbox"
                checked={substituir}
                onChange={(evento) => setSubstituir(evento.target.checked)}
              />
              Trocar o texto atual pelo colado (desmarque para acrescentar no fim)
            </label>

            <Button onClick={montar} disabled={lido.contagem.palavras === 0}>
              <Wand2 />
              {livro ? 'Montar os capítulos' : 'Montar as seções'}
            </Button>
          </div>
        ) : (
          <p className="text-xs texto-suave">
            Nada foi colado ainda. O texto colado não é enviado para lugar nenhum: a leitura acontece aqui, na sua
            tela.
          </p>
        )}

        {/*
          O caminho de volta. O documento inteiro sai em Markdown — para levar num
          e-mail, guardar em arquivo ou abrir em outro editor — e volta inteiro
          pelo mesmo caminho de leitura desta caixa.
        */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <Button variant="contorno" tamanho="pequeno" onClick={() => void copiar()} disabled={!temTexto}>
            {copiado ? <Check /> : <FileDown />}
            {copiado ? 'Markdown copiado' : 'Copiar o texto em Markdown'}
          </Button>
          <Button variant="contorno" tamanho="pequeno" onClick={baixar} disabled={!temTexto}>
            <FileDown />
            Baixar o arquivo .md
          </Button>
          <span className="text-xs texto-suave">
            Sai a estrutura toda (títulos, citações, resumo e referências) — cole de volta aqui para continuar depois.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Nome amigável das peças que a leitura reconhece (dedicatória, epígrafe...). */
const ROTULO_CAMPO_LIDO = {
  dedicatoria: 'dedicatória',
  agradecimentos: 'agradecimentos',
  epigrafe: 'epígrafe',
  apresentacao: 'apresentação',
  epilogo: 'epílogo',
  sobreOAutor: 'sobre o autor',
} as const;

/**
 * A legenda e a fonte das **tabelas** — o que o editor não escreve.
 *
 * A grade da tabela se escreve no editor, que tem o diálogo dele (acrescentar linha,
 * coluna, cabeçalho, apagar célula). O que ele não tem é a **legenda numerada em cima**
 * ("Tabela 1 — …") e a **fonte embaixo** ("Fonte: …"), que a norma pede e o papel
 * imprime — são atributos da tabela no documento. São dois campos por tabela, e a
 * lista só aparece quando o documento tem alguma.
 */
/**
 * Os blocos de um tipo, seção por seção — o que os cartões de legenda, de figura e de nota
 * precisam para mexer num bloco só.
 */
function blocosDeTipo(
  documento: DocumentoAcademico,
  tipo: BlocoDeTexto['tipo'],
): Array<{ secao: SecaoDocumento; bloco: BlocoDeTexto }> {
  return documento.secoes.flatMap((secao) =>
    blocosDaSecao(secao)
      .filter((bloco) => bloco.tipo === tipo)
      .map((bloco) => ({ secao, bloco })),
  );
}

/** Mexe num bloco só, pelo id da seção e o do bloco. */
function mexerNoBloco(
  editar: Editar,
  secaoId: string,
  blocoId: string,
  parcial: Partial<BlocoDeTexto>,
) {
  editar((atual) => ({
    ...atual,
    secoes: atual.secoes.map((secao) =>
      secao.id === secaoId
        ? {
            ...secao,
            blocos: blocosDaSecao(secao).map((bloco) =>
              bloco.id === blocoId ? { ...bloco, ...parcial } : bloco,
            ),
          }
        : secao,
    ),
  }));
}

/**
 * A legenda, a fonte e a largura de cada **figura** do texto.
 *
 * A legenda sai **acima** da imagem e a fonte **abaixo**, como a NBR 14724 pede para a
 * ilustração — e quem numera é o motor (a ordem das figuras no documento), como nas tabelas.
 * A largura é a medida impressa em milímetros: quem escolhe é quem escreve, e o papel limita
 * à mancha.
 */
function CartaoDeFiguras({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const figuras = blocosDeTipo(documento, 'IMAGEM');
  if (figuras.length === 0) return null;

  return (
    <details className="rounded-lg border border-border bg-card p-3" data-figuras="">
      <summary className="cursor-pointer text-sm font-medium">
        Legenda, fonte e tamanho das figuras ({figuras.length})
      </summary>
      <div className="space-y-4 pt-3">
        {figuras.map(({ secao, bloco }, indice) => (
          <div key={bloco.id} className="space-y-2 rounded-md border border-border/70 p-2">
            <p className="text-xs font-semibold uppercase tracking-wide texto-suave">
              Figura {indice + 1} · {secao.titulo.trim() || 'seção sem título'}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                rotulo="Legenda"
                dica="Sai numerada acima da imagem: Figura 1 — ..."
                className="sm:col-span-2"
              >
                <input
                  value={bloco.legenda ?? ''}
                  aria-label={`Legenda da figura ${indice + 1}`}
                  onChange={(evento) => mexerNoBloco(editar, secao.id, bloco.id, { legenda: evento.target.value })}
                  placeholder="Sala de aula com recursos digitais"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
              <Campo rotulo="Fonte" dica="Sai abaixo da imagem: Fonte: IBGE (2020).">
                <input
                  value={bloco.fonte ?? ''}
                  aria-label={`Fonte da figura ${indice + 1}`}
                  onChange={(evento) => mexerNoBloco(editar, secao.id, bloco.id, { fonte: evento.target.value })}
                  placeholder="Acervo do autor (2026)"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
              <Campo rotulo="Largura impressa (mm)" dica="O teto é a largura da mancha da folha.">
                <input
                  type="number"
                  min={10}
                  max={210}
                  value={bloco.larguraMm ?? LARGURA_DE_IMAGEM_MM}
                  aria-label={`Largura da figura ${indice + 1}`}
                  onChange={(evento) => {
                    const medida = Number.parseInt(evento.target.value, 10);
                    mexerNoBloco(editar, secao.id, bloco.id, {
                      larguraMm: Number.isFinite(medida) && medida >= 10 ? medida : undefined,
                    });
                  }}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * O texto de cada **nota** e onde a lista delas sai.
 *
 * No texto a nota é só o número; quem escreve o texto da nota escreve aqui. O navegador não
 * imprime nota no pé da página, então a escolha é entre o **fim da seção** (o mais perto do
 * rodapé que se chega, e o padrão) e o **fim do documento** — a nota de fim.
 */
function CartaoDeNotas({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const notas = blocosDeTipo(documento, 'NOTA');
  if (notas.length === 0) return null;

  const numeros = numerosDasNotas(documento);
  const onde = posicaoDasNotas(documento);

  return (
    <details className="rounded-lg border border-border bg-card p-3" data-notas="">
      <summary className="cursor-pointer text-sm font-medium">
        Notas de rodapé e de fim ({notas.length})
      </summary>
      <div className="space-y-4 pt-3">
        <Campo rotulo="Onde as notas saem" dica={DESCRICAO_POSICAO_DAS_NOTAS[onde]}>
          <Select
            value={onde}
            onValueChange={(valor) =>
              editar((atual) => ({ ...atual, notas: valor as PosicaoDasNotas }))
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {POSICOES_DAS_NOTAS.map((item) => (
                <SelectItem key={item} value={item}>
                  {ROTULO_POSICAO_DAS_NOTAS[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        {notas.map(({ secao, bloco }) => {
          const numero = numeros.get(bloco.id) ?? 0;
          return (
            <div key={bloco.id} className="space-y-2 rounded-md border border-border/70 p-2">
              <p className="text-xs font-semibold uppercase tracking-wide texto-suave">
                Nota {numero} · {secao.titulo.trim() || 'seção sem título'}
              </p>
              <Campo rotulo="Texto da nota" dica="A citação entra aqui inteira, como na nota de rodapé.">
                <textarea
                  value={bloco.texto ?? ''}
                  aria-label={`Texto da nota ${numero}`}
                  onChange={(evento) => mexerNoBloco(editar, secao.id, bloco.id, { texto: evento.target.value })}
                  rows={2}
                  placeholder="SOUZA, Ana. Alfabetizar com tela. São Paulo: Editora Nova, 2024. p. 15."
                  className="w-full rounded-md border border-input bg-background p-2 text-sm"
                />
              </Campo>
            </div>
          );
        })}
      </div>
    </details>
  );
}

function CartaoDeTabelas({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const tabelas = documento.secoes.flatMap((secao) =>
    blocosDaSecao(secao)
      .filter((bloco) => bloco.tipo === 'TABELA')
      .map((bloco) => ({ secao, bloco })),
  );
  if (tabelas.length === 0) return null;

  /** Mexe num bloco só: é o que os dois campos fazem. */
  const mudar = (secaoId: string, blocoId: string, parcial: Partial<BlocoDeTexto>) =>
    editar((atual) => ({
      ...atual,
      secoes: atual.secoes.map((secao) =>
        secao.id === secaoId
          ? {
              ...secao,
              blocos: blocosDaSecao(secao).map((bloco) =>
                bloco.id === blocoId ? { ...bloco, ...parcial } : bloco,
              ),
            }
          : secao,
      ),
    }));

  return (
    <details className="rounded-lg border border-border bg-card p-3" data-tabelas="">
      <summary className="cursor-pointer text-sm font-medium">
        Legenda e fonte das tabelas ({tabelas.length})
      </summary>
      <div className="space-y-4 pt-3">
        {tabelas.map(({ secao, bloco }, indice) => (
          <div key={bloco.id} className="space-y-2 rounded-md border border-border/70 p-2">
            <p className="text-xs font-semibold uppercase tracking-wide texto-suave">
              Tabela {indice + 1} · {secao.titulo.trim() || 'seção sem título'}
            </p>
            <Campo
              rotulo="Legenda"
              dica="Sai numerada em cima da tabela: Tabela 1 — ... (a norma pede o título acima dos dados)."
            >
              <input
                value={bloco.legenda ?? ''}
                aria-label={`Legenda da tabela ${indice + 1}`}
                onChange={(evento) => mudar(secao.id, bloco.id, { legenda: evento.target.value })}
                placeholder="Resultados da primeira etapa"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>
            <Campo rotulo="Fonte" dica="Sai embaixo da tabela: Fonte: ... (de onde vieram os dados).">
              <input
                value={bloco.fonte ?? ''}
                aria-label={`Fonte da tabela ${indice + 1}`}
                onChange={(evento) => mudar(secao.id, bloco.id, { fonte: evento.target.value })}
                placeholder="IBGE (2023)"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * A aba da folha: o que sai **fora do texto**.
 *
 * Três assuntos, na ordem em que a pessoa pensa neles: a faixa que se repete
 * (cabeçalho, rodapé, número), como o capítulo abre a folha e o que sobra de
 * folha em branco para escrever à mão. Tudo é opcional e o padrão já é o do
 * modelo — quem não mexer não perde nada.
 */
function PainelFolha({
  documento,
  editar,
  livro,
  aoMarcarAnotacao,
}: {
  documento: DocumentoAcademico;
  editar: Editar;
  /** No livro fala-se de capítulo; no trabalho, de seção principal. */
  livro: boolean;
  /** Marca (ou desmarca) a peça das folhas de anotação. */
  aoMarcarAnotacao: () => void;
}) {
  const folha = folhaDoDocumento(documento);
  const formato = formatoDoCapitulo(documento);
  const temAnotacao = documento.itens.includes('PAGINA_DE_ANOTACAO');

  /*
   * A escolha é gravada **inteira** (o motor devolve o padrão onde falta): assim
   * o documento salvo diz por extenso o que vai sair impresso, e uma mudança de
   * padrão amanhã não mexe em quem já escolheu.
   */
  const mudarFolha = (parcial: Partial<FolhaDoDocumento>) =>
    editar((atual) => ({ ...atual, folha: { ...folhaDoDocumento(atual), ...parcial } }));

  const mudarCapitulo = (parcial: Partial<FormatoDeCapitulo>) =>
    editar((atual) => ({ ...atual, capitulo: { ...formatoDoCapitulo(atual), ...parcial } }));

  const estilo = estiloDaFaixa(documento);

  /**
   * A escolha do estilo é gravada **inteira** (o motor devolve o padrão onde
   * falta): quem abrir o documento depois vê exatamente o que foi impresso.
   */
  const mudarEstilo = (parcial: Partial<EstiloDaFaixa>) =>
    editar((atual) => ({
      ...atual,
      folha: { ...folhaDoDocumento(atual), estiloDaFaixa: { ...estiloDaFaixa(atual), ...parcial } },
    }));

  const alternarEstilo = (campo: 'negrito' | 'italico' | 'sublinhado' | 'versalete' | 'caixaAlta') =>
    mudarEstilo({ [campo]: !estilo[campo] });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Cabeçalho, rodapé e número de página</CardTitle>
          <CardDescription>
            O que se repete fora do texto, em todas as folhas impressas. A capa, a folha de rosto, o resumo e
            o sumário saem <strong>limpos</strong>: o número e o cabeçalho só aparecem a partir do texto —
            como manda a norma e como faz o livro impresso. A contagem continua correndo desde a capa, então
            o primeiro número impresso é o da posição real da folha.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Cabeçalho" dica={DESCRICAO_ESTILO_DE_CABECALHO[folha.cabecalho]}>
              <Select
                value={folha.cabecalho}
                onValueChange={(valor) => mudarFolha({ cabecalho: valor as FolhaDoDocumento['cabecalho'] })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESTILOS_DE_CABECALHO.map((estilo) => (
                    <SelectItem key={estilo} value={estilo}>
                      {ROTULO_ESTILO_DE_CABECALHO[estilo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo rotulo="Rodapé" dica={DESCRICAO_ESTILO_DE_RODAPE[folha.rodape]}>
              <Select
                value={folha.rodape}
                onValueChange={(valor) => mudarFolha({ rodape: valor as FolhaDoDocumento['rodape'] })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESTILOS_DE_RODAPE.map((estilo) => (
                    <SelectItem key={estilo} value={estilo}>
                      {ROTULO_ESTILO_DE_RODAPE[estilo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
          </div>

          {folha.cabecalho === 'LIVRE' ? (
            <>
              <Campo
                rotulo="Texto do cabeçalho"
                dica="O que sai no alto de todas as folhas. Pode colar de onde quiser; a formatação do original não vem junto — o estilo da faixa é o que vale. Escrever em duas linhas quebra a linha no papel."
              >
                <textarea
                  value={folha.textoDoCabecalho ?? ''}
                  onChange={(evento) => mudarFolha({ textoDoCabecalho: evento.target.value })}
                  rows={2}
                  placeholder={'Ex.: Coleção Azul · volume 2\nCadernos do Norte'}
                  className="w-full rounded-md border border-input bg-background p-3 text-sm"
                />
              </Campo>
              <Campo rotulo="Posição do cabeçalho" dica="Onde o seu texto fica na largura da folha.">
                <Select
                  value={folha.posicaoDoCabecalho ?? 'CENTRO'}
                  onValueChange={(valor) =>
                    mudarFolha({ posicaoDoCabecalho: valor as FolhaDoDocumento['posicaoDoCabecalho'] })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSICOES_DA_FAIXA.map((posicao) => (
                      <SelectItem key={posicao} value={posicao}>
                        {ROTULO_POSICAO_DA_FAIXA[posicao]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            </>
          ) : null}

          {folha.rodape === 'LIVRE' ? (
            <>
              <Campo
                rotulo="Texto do rodapé"
                dica="O que sai no pé de todas as folhas. Vale o mesmo: cole o texto e escolha o estilo abaixo."
              >
                <textarea
                  value={folha.textoDoRodape ?? ''}
                  onChange={(evento) => mudarFolha({ textoDoRodape: evento.target.value })}
                  rows={2}
                  placeholder="Ex.: Editora Miolos · 2026"
                  className="w-full rounded-md border border-input bg-background p-3 text-sm"
                />
              </Campo>
              <Campo rotulo="Posição do rodapé" dica="Onde o seu texto fica na largura da folha.">
                <Select
                  value={folha.posicaoDoRodape ?? 'CENTRO'}
                  onValueChange={(valor) =>
                    mudarFolha({ posicaoDoRodape: valor as FolhaDoDocumento['posicaoDoRodape'] })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSICOES_DA_FAIXA.map((posicao) => (
                      <SelectItem key={posicao} value={posicao}>
                        {ROTULO_POSICAO_DA_FAIXA[posicao]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            </>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Número de página" dica={DESCRICAO_POSICAO_DO_NUMERO[folha.numeracao]}>
              <Select
                value={folha.numeracao}
                onValueChange={(valor) => mudarFolha({ numeracao: valor as FolhaDoDocumento['numeracao'] })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSICOES_DO_NUMERO.map((posicao) => (
                    <SelectItem key={posicao} value={posicao}>
                      {ROTULO_POSICAO_DO_NUMERO[posicao]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo rotulo="Corpo das faixas (pt)" dica="O corpo do cabeçalho, do rodapé e do número.">
              <input
                type="number"
                min={6}
                max={12}
                step={0.5}
                aria-label="Corpo das faixas, em pontos"
                value={folha.corpoPt}
                onChange={(evento) => {
                  const valor = Number(evento.target.value);
                  mudarFolha({ corpoPt: Number.isFinite(valor) ? Math.min(12, Math.max(6, valor)) : 8 });
                }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={folha.filete}
              onChange={(evento) => mudarFolha({ filete: evento.target.checked })}
            />
            Filete: a linha fina embaixo do cabeçalho, atravessando a folha
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Estilo das faixas</CardTitle>
          <CardDescription>
            Como o texto do cabeçalho e do rodapé sai impresso — negrito, itálico, sublinhado,
            versalete, caixa alta, corpo, cor, espaçamento e letra.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* A faixa no corpo de verdade (a prévia da folha está reduzida). */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Como vai sair</span>
            <div className="space-y-3 rounded-md border border-dashed border-border bg-background p-3">
              <FaixaDaPrevia documento={documento} qual="cabecalho" />
              <FaixaDaPrevia documento={documento} qual="rodape" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <BotaoDeEstilo
              rotulo="Negrito"
              classe="font-bold"
              ativo={estilo.negrito}
              aoAlternar={() => alternarEstilo('negrito')}
            />
            <BotaoDeEstilo
              rotulo="Itálico"
              classe="italic"
              ativo={estilo.italico}
              aoAlternar={() => alternarEstilo('italico')}
            />
            <BotaoDeEstilo
              rotulo="Sublinhado"
              classe="underline"
              ativo={estilo.sublinhado}
              aoAlternar={() => alternarEstilo('sublinhado')}
            />
            <BotaoDeEstilo
              rotulo="Versalete"
              classe="[font-variant:small-caps]"
              ativo={estilo.versalete}
              aoAlternar={() => alternarEstilo('versalete')}
            />
            <BotaoDeEstilo
              rotulo="Caixa alta"
              classe="uppercase"
              ativo={estilo.caixaAlta}
              aoAlternar={() => alternarEstilo('caixaAlta')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Campo rotulo="Letra" dica="A letra da faixa.">
              <Select
                value={estilo.fonte}
                onValueChange={(valor) => mudarEstilo({ fonte: valor as FonteDaFaixa })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONTES_DA_FAIXA.map((fonte) => (
                    <SelectItem key={fonte} value={fonte}>
                      {ROTULO_FONTE_DA_FAIXA[fonte]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo rotulo="Cor" dica="Para a faixa discreta, um cinza escuro.">
              <input
                type="color"
                aria-label="Cor da faixa"
                value={estilo.cor}
                onChange={(evento) => mudarEstilo({ cor: evento.target.value })}
                className="h-10 w-full rounded-md border border-input bg-background p-1 sm:h-9"
              />
            </Campo>

            <Campo rotulo="Espaçamento entre letras" dica="0 é normal; 20 deixa a faixa frouxa.">
              <input
                type="range"
                min={0}
                max={30}
                step={1}
                aria-label="Espaçamento entre letras, em centésimos de em"
                value={estilo.espacamento}
                onChange={(evento) => mudarEstilo({ espacamento: Number(evento.target.value) })}
                className="h-10 w-full sm:h-9"
              />
            </Campo>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs leading-relaxed text-muted-foreground">
              A faixa sai com <strong>um estilo só</strong>, do começo ao fim: negrito em parte do
              texto não é possível, porque a margem da folha aceita apenas texto corrido. Duas
              linhas funcionam: a faixa quebra onde você quebrar.
            </p>
            <Button variant="fantasma" tamanho="pequeno" onClick={() => mudarEstilo(estiloDaFaixaPadrao())}>
              Limpar formatação
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quebra de folha</CardTitle>
          <CardDescription>
            Onde o papel vira. Além desta regra, cada seção tem a sua própria marca (“Começa em folha nova”,
            na aba Texto) — útil para uma parte de dentro do capítulo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={folha.capituloEmFolhaNova}
              onChange={(evento) => mudarFolha({ capituloEmFolhaNova: evento.target.checked })}
            />
            {livro ? 'Cada capítulo começa em folha nova' : 'Cada seção principal começa em folha nova'}
          </label>
          <p className="text-xs leading-relaxed text-muted-foreground">
            O título nunca fica sozinho no pé da folha: se não couber com o primeiro parágrafo, ele passa
            inteiro para a folha seguinte.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Como o capítulo abre</CardTitle>
          <CardDescription>
            {livro
              ? 'O modelo do capítulo: se o número aparece e de que forma, onde o título fica, quanto ele é maior que o texto e quanto respiro tem do alto da folha.'
              : 'O modelo da seção principal. As subseções continuam com a numeração progressiva da norma (1.1, 1.1.1) — é o que a banca procura.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Rótulo do capítulo" dica={DESCRICAO_ROTULO_DE_CAPITULO[formato.rotulo]}>
              <Select
                value={formato.rotulo}
                onValueChange={(valor) => mudarCapitulo({ rotulo: valor as FormatoDeCapitulo['rotulo'] })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROTULOS_DE_CAPITULO.map((rotulo) => (
                    <SelectItem key={rotulo} value={rotulo}>
                      {ROTULO_ROTULO_DE_CAPITULO[rotulo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo rotulo="Posição do título" dica={DESCRICAO_ALINHAMENTO_DE_CAPITULO[formato.alinhamento]}>
              <Select
                value={formato.alinhamento}
                onValueChange={(valor) =>
                  mudarCapitulo({ alinhamento: valor as FormatoDeCapitulo['alinhamento'] })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALINHAMENTOS_DE_CAPITULO.map((alinhamento) => (
                    <SelectItem key={alinhamento} value={alinhamento}>
                      {ROTULO_ALINHAMENTO_DE_CAPITULO[alinhamento]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Título maior que o texto (pt)" dica="0 mantém o título do tamanho do texto.">
              <input
                type="number"
                min={0}
                max={14}
                aria-label="Título maior que o texto, em pontos"
                value={formato.aumentoPt}
                onChange={(evento) => {
                  const valor = Number(evento.target.value);
                  mudarCapitulo({ aumentoPt: Number.isFinite(valor) ? Math.min(14, Math.max(0, valor)) : 0 });
                }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>

            <Campo rotulo="Respiro antes do título (mm)" dica="Quanto o título desce do alto da mancha.">
              <input
                type="number"
                min={0}
                max={80}
                aria-label="Respiro antes do título, em milímetros"
                value={formato.respiroAntesMm}
                onChange={(evento) => {
                  const valor = Number(evento.target.value);
                  mudarCapitulo({
                    respiroAntesMm: Number.isFinite(valor) ? Math.min(80, Math.max(0, valor)) : 0,
                  });
                }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>
          </div>

          {/*
           * A caixa alta do título **não é escolha**: no trabalho e no artigo é a norma,
           * e no livro é a regra do modelo (o capítulo abre em caixa alta; as partes de
           * dentro saem como foram escritas). O campo fica à vista, ligado e travado,
           * porque é isso que a folha faz — um botão que não faz nada seria pior que
           * botão nenhum.
           */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked disabled />
            Título do capítulo em caixa alta
            <span className="text-xs texto-suave">(sai assim de qualquer forma)</span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formato.tituloIsolado}
              onChange={(evento) => mudarCapitulo({ tituloIsolado: evento.target.checked })}
            />
            Título sozinho na folha: o texto do capítulo começa na folha seguinte
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(documento.capitular)}
              onChange={(evento) => editar((atual) => ({ ...atual, capitular: evento.target.checked }))}
            />
            Letra capitulada no primeiro parágrafo
          </label>
          <p className="text-xs leading-relaxed text-muted-foreground">
            A capitular é a primeira letra do capítulo em corpo grande, com as três primeiras linhas
            subindo ao lado dela.
          </p>

          {/*
            O destaque de cada nível é da norma, e o gerador aplica sozinho: quem
            precisa saber disso é quem escreve, para não estranhar a diferença no
            papel (ou para não tentar “consertar” o itálico da subparte).
          */}
          <div className="secao-tingida rounded-lg border border-border p-3" style={{ ['--tom' as string]: 'var(--cor-alegre-2)' }}>
            <p className="text-sm font-medium">A norma destaca cada nível de um jeito</p>
            <ul className="mt-1 space-y-0.5 text-xs leading-relaxed texto-suave">
              <li>
                <strong>1 INTRODUÇÃO</strong> — seção primária: maiúsculas e negrito
              </li>
              <li>
                <strong>1.1 Revisão de literatura</strong> — subseção: negrito
              </li>
              <li>
                <em>1.1.1 Detalhes finos</em> — subparte: itálico
              </li>
            </ul>
            <p className="mt-1 text-xs leading-relaxed texto-suave">
              O destaque é aplicado pelo gerador, pela numeração — e não pelo que estiver escrito. A
              caixa das subseções, essa, é sua: em CAIXA ALTA a norma preferiria só a primeira letra,
              e o gerador avisa no lugar de reescrever.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Folhas de anotação</CardTitle>
          <CardDescription>
            Folhas para escrever à mão depois de impresso: pauta de caderno, grade de bolinhas ou folha
            lisa. Elas entram <strong>antes de cada capítulo</strong> (ou todas juntas no fim, à sua
            escolha), usam a <strong>folha inteira</strong>, com margem estreita, e saem sem cabeçalho,
            rodapé e número — é folha de escrever, não página de texto.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={temAnotacao} onChange={aoMarcarAnotacao} />
            O documento leva folhas de anotação
          </label>

          {temAnotacao ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Quantas folhas"
                dica={`Até ${MAXIMO_FOLHAS_DE_ANOTACAO} folhas em cada lugar — e no trabalho com capítulos o jogo de folhas se repete antes de cada um.`}
              >
                <input
                  type="number"
                  min={0}
                  max={MAXIMO_FOLHAS_DE_ANOTACAO}
                  aria-label="Quantas folhas de anotação"
                  value={documento.folhasDeAnotacao ?? 1}
                  onChange={(evento) => {
                    const valor = Number(evento.target.value);
                    editar((atual) => ({
                      ...atual,
                      folhasDeAnotacao: Number.isFinite(valor)
                        ? Math.min(MAXIMO_FOLHAS_DE_ANOTACAO, Math.max(0, Math.floor(valor)))
                        : 1,
                    }));
                  }}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>

              <Campo
                rotulo="Onde entram"
                dica={DESCRICAO_POSICAO_DA_ANOTACAO[posicaoDaAnotacao(documento)]}
              >
                <Select
                  value={posicaoDaAnotacao(documento)}
                  onValueChange={(valor) =>
                    editar((atual) => ({ ...atual, anotacaoPosicao: valor as PosicaoDaAnotacao }))
                  }
                >
                  {/*
                    O rótulo visível é texto puro (`Campo`), e não um `label` ligado ao
                    campo: sem este `aria-label`, quem usa leitor de tela — e o teste —
                    procura o campo pelo nome e não acha.
                  */}
                  <SelectTrigger className="w-full" aria-label="Onde entram as folhas de anotação">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSICOES_DA_ANOTACAO.map((posicao) => (
                      <SelectItem key={posicao} value={posicao}>
                        {ROTULO_POSICAO_DA_ANOTACAO[posicao]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>

              <Campo
                rotulo="Como são pautadas"
                dica={DESCRICAO_FORMATO_DE_ANOTACAO[documento.estiloDeAnotacao ?? 'LINHAS']}
              >
                <Select
                  value={documento.estiloDeAnotacao ?? 'LINHAS'}
                  onValueChange={(valor) =>
                    editar((atual) => ({ ...atual, estiloDeAnotacao: valor as FormatoDeAnotacao }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMATOS_DE_ANOTACAO.map((formatoDeAnotacao) => (
                      <SelectItem key={formatoDeAnotacao} value={formatoDeAnotacao}>
                        {ROTULO_FORMATO_DE_ANOTACAO[formatoDeAnotacao]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            </div>
          ) : (
            <p className="text-xs leading-relaxed text-muted-foreground">
              Marque a caixa acima para o documento terminar com folhas pautadas.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * A faixa como ela sai — em corpo de papel, fora da escala da prévia.
 *
 * A prévia do documento está reduzida para caber a folha inteira na tela; julgar
 * o estilo de um cabeçalho de 8 pt ali não dá. Aqui a faixa aparece no corpo
 * verdadeiro, com as três posições (esquerda, centro e direita) — que é onde o
 * texto próprio, o título e o número podem cair.
 */
function FaixaDaPrevia({ documento, qual }: { documento: DocumentoAcademico; qual: 'cabecalho' | 'rodape' }) {
  const faixas = molduraDaFolha(documento);
  const faixa = qual === 'cabecalho' ? faixas.comum.alto : faixas.comum.pe;

  const texto = (caixa: CaixaDaFolha) => {
    if (caixa.tipo === 'TEXTO') return caixa.texto;
    if (caixa.tipo === 'NUMERO') return 'nº';
    return '';
  };

  return (
    <div
      className="flex items-center justify-between gap-2 rounded border border-dashed border-border/70 px-2 py-1"
      style={estiloDaFaixaEmCss(documento)}
    >
      <span>{texto(faixa.esquerda)}</span>
      <span>{texto(faixa.centro)}</span>
      <span>{texto(faixa.direita)}</span>
    </div>
  );
}

/** Um botão da barra de estilo da faixa (negrito, itálico, versalete...). */
function BotaoDeEstilo({
  rotulo,
  classe,
  ativo,
  aoAlternar,
}: {
  rotulo: string;
  classe: string;
  ativo: boolean;
  aoAlternar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoAlternar}
      aria-pressed={ativo}
      title={rotulo}
      className={[
        'h-10 rounded-md border px-3 text-sm transition-colors disabled:opacity-40',
        classe,
        ativo ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:border-primary/40',
      ].join(' ')}
    >
      {rotulo}
    </button>
  );
}

function PainelAnexos({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const enviar = async (arquivo: File) => {
    setEnviando(true);
    setErro('');
    try {
      const { arquivo: enviado } = await api.enviarArquivo(arquivo);
      editar((atual) => ({
        ...atual,
        anexos: [...(atual.anexos ?? []), { id: enviado.id, nome: arquivo.name, url: enviado.url }],
      }));
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível enviar o anexo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Anexos</CardTitle>
        <CardDescription>
          Envie os PDFs que embasam o trabalho (norma, laudo, contrato...). Cada anexo entra no fim,
          em folha própria, com a letra (Anexo A, Anexo B...).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <input
          type="file"
          accept="application/pdf"
          disabled={enviando}
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            if (arquivo) void enviar(arquivo);
            evento.target.value = '';
          }}
          className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground"
        />
        {enviando ? <p className="text-xs texto-suave">Enviando...</p> : null}
        {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

        {(documento.anexos ?? []).length > 0 ? (
          <ul className="space-y-2">
            {(documento.anexos ?? []).map((anexo, indice) => (
              <li
                key={anexo.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
              >
                <span className="min-w-0 truncate text-sm">
                  <strong>Anexo {String.fromCharCode(65 + indice)}</strong> · {anexo.nome}
                </span>
                <Button
                  variant="contorno"
                  tamanho="pequeno"
                  onClick={() =>
                    editar((atual) => ({
                      ...atual,
                      anexos: (atual.anexos ?? []).filter((item) => item.id !== anexo.id),
                    }))
                  }
                >
                  <Trash2 />
                  Remover
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs texto-suave">Nenhum anexo ainda. Envie um PDF para começar.</p>
        )}
      </CardContent>
    </Card>
  );
}

function PainelFiguras({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const [enviando, setEnviando] = useState('');

  const mudarFigura = (indice: number, parcial: Partial<FiguraDocumento>) =>
    editar((atual) => ({
      ...atual,
      figuras: atual.figuras.map((figura, i) => (i === indice ? { ...figura, ...parcial } : figura)),
    }));

  async function enviar(indice: number, arquivo: File) {
    setEnviando(`figura-${indice}`);
    try {
      const { arquivo: enviado } = await api.enviarImagem(arquivo);
      mudarFigura(indice, { url: enviado.url });
    } finally {
      setEnviando('');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Figuras</CardTitle>
        <CardDescription>
          Cada figura sai com legenda e fonte embaixo, como a norma pede — e entra na lista de figuras se
          você marcar aquele item. A imagem é enviada do seu computador;
          {" "}PNG e JPG, até 8 MB.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {documento.figuras.map((figura, indice) => (
          <section key={figura.id} className="space-y-3 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Figura {indice + 1}</span>
              <Button
                variant="fantasma"
                tamanho="pequeno"
                onClick={() =>
                  editar((atual) => ({ ...atual, figuras: atual.figuras.filter((_, i) => i !== indice) }))
                }
              >
                <Trash2 />
              </Button>
            </div>

            <Campo rotulo="Legenda" dica="Como a figura é chamada no texto: “Fluxo da pesquisa”.">
              <input
                value={figura.legenda}
                onChange={(evento) => mudarFigura(indice, { legenda: evento.target.value })}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>

            <Campo rotulo="Fonte" dica="De onde a figura veio (ou “Elaborado pelo autor”).">
              <input
                value={figura.fonte}
                onChange={(evento) => mudarFigura(indice, { fonte: evento.target.value })}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
              />
            </Campo>

            <div className="grid gap-3 sm:grid-cols-2">
              <Campo rotulo="Largura na página (mm)">
                <input
                  type="number"
                  min={40}
                  max={160}
                  value={figura.larguraMm}
                  onChange={(evento) => mudarFigura(indice, { larguraMm: Number(evento.target.value) })}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
              <Campo rotulo="Imagem">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(evento) => {
                    const arquivo = evento.target.files?.[0];
                    if (arquivo) void enviar(indice, arquivo);
                  }}
                  className="block w-full text-xs"
                />
              </Campo>
            </div>

            {enviando === `figura-${indice}` ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                Enviando a imagem...
              </p>
            ) : null}

            {figura.url ? (
              <img src={urlDaImagem(figura.url)} alt="" className="max-h-40 rounded border border-border object-contain" />
            ) : null}
          </section>
        ))}

        <Button
          variant="contorno"
          onClick={() =>
            editar((atual) => ({
              ...atual,
              figuras: [
                ...atual.figuras,
                {
                  id: `figura-${Date.now().toString(36)}`,
                  secao: atual.secoes[0]?.id ?? '',
                  legenda: '',
                  fonte: '',
                  url: null,
                  larguraMm: 120,
                },
              ],
            }))
          }
        >
          <ImagePlus />
          Nova figura
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * A caixa de **trazer referências de fora**.
 *
 * Quem escreve já tem a lista pronta em algum lugar — Zotero, Mendeley, o site da
 * revista, o Google Acadêmico, a ficha do livro na livraria. Aqui o texto colado (BibTeX,
 * RIS, um ISBN ou um DOI) é lido pelo servidor e volta como referência do gerador,
 * **mostrada antes de entrar na lista**: quem cola confere o que foi entendido, e só
 * então acrescenta. Duas etapas de propósito — uma referência errada na lista é o defeito
 * mais caro de achar depois.
 *
 * A mesma conferência evita o trabalho dobrado: o que já está na lista vem marcado
 * (**já está na lista**) e não entra de novo. Quem cola a mesma lista duas vezes — o que
 * acontece — não termina com o dobro de referências.
 *
 * Mobile primeiro: a caixa de texto ocupa a largura toda, o botão também (no celular ele
 * fica embaixo do polegar) e a conferência vem empilhada, em uma linha por referência.
 */
function CaixaDeImportarReferencias({
  jaNaLista,
  aoAcrescentar,
}: {
  /** O que o documento já tem: a conferência da repetida é feita no motor. */
  jaNaLista: ReferenciaDocumento[];
  aoAcrescentar: (referencias: Array<Omit<ReferenciaDocumento, 'id'>>) => void;
}) {
  const [texto, setTexto] = useState('');
  const [lidas, setLidas] = useState<ReferenciaLida[]>([]);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');

  const ler = async () => {
    setLendo(true);
    setErro('');
    setAviso('');
    try {
      const resposta = await api.importarReferencias(texto);
      setLidas(marcarReferenciasRepetidas(resposta.referencias, jaNaLista));
      if (resposta.referencias.length === 0) setErro('O texto não trouxe referência nenhuma.');
    } catch (causa) {
      setLidas([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível ler o texto agora.');
    } finally {
      setLendo(false);
    }
  };

  const novas = lidas
    .filter((item) => !item.repetida)
    .map(({ repetida: _repetida, ...referencia }) => referencia);
  const repetidas = lidas.length - novas.length;

  const acrescentar = () => {
    aoAcrescentar(novas);
    setAviso(
      novas.length === 1
        ? 'A referência entrou na lista.'
        : `${novas.length} referências entraram na lista.`,
    );
    setLidas([]);
    setTexto('');
    setErro('');
  };

  return (
    <section
      className="space-y-3 rounded-lg border border-dashed border-border p-3"
      data-importar-referencias=""
    >
      <div className="space-y-1">
        <p className="text-sm font-medium">Trazer de fora: BibTeX, RIS, ISBN ou DOI</p>
        <p className="text-xs leading-relaxed texto-suave">
          Copie do Zotero, do Mendeley, do site da revista, do Google Acadêmico ou a ficha do livro e cole
          aqui. O gerador lê e monta a referência na norma — você confere antes de acrescentar à lista.
        </p>
      </div>

      <textarea
        value={texto}
        onChange={(evento) => {
          setTexto(evento.target.value);
          setLidas([]);
          setErro('');
          setAviso('');
        }}
        rows={5}
        aria-label="Referências coladas"
        placeholder={
          '@article{silva2024,\n  author = {Silva, João},\n  title = {Alfabetizar com tela},\n  journal = {Revista Brasileira de Educação},\n  year = {2024}\n}\n\nISBN: 9780262033848\n\nou só o DOI: 10.1590/1980-220x-reeusp-2024-0001'
        }
        className="w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
      />

      <Button
        className="w-full sm:w-auto"
        disabled={lendo || texto.trim().length < 2}
        onClick={() => void ler()}
        data-ler-referencias=""
      >
        <Wand2 />
        {lendo ? 'Lendo...' : 'Ler referências'}
      </Button>

      {erro ? (
        <p className="text-xs text-destructive" data-erro-das-referencias="">
          {erro}
        </p>
      ) : null}

      {aviso ? (
        <p className="text-xs texto-suave" data-aviso-das-referencias="">
          {aviso}
        </p>
      ) : null}

      {lidas.length > 0 ? (
        <div className="space-y-2 rounded-md border border-border bg-background/70 p-3" data-referencias-lidas="">
          <p className="text-xs font-medium">
            Entendi {lidas.length} {lidas.length === 1 ? 'referência' : 'referências'}
            {repetidas > 0
              ? ` — ${repetidas} já ${repetidas === 1 ? 'está' : 'estão'} na lista:`
              : ':'}
          </p>
          <ul className="space-y-1.5 text-xs leading-relaxed texto-suave">
            {lidas.map((item, indice) => (
              <li
                key={`${item.titulo}-${indice}`}
                className="flex flex-wrap gap-x-2 gap-y-1"
                data-repetida={item.repetida ? 'sim' : 'nao'}
              >
                <span className="mt-1 size-1.5 shrink-0 rounded-full bg-current opacity-50" />
                <span className="min-w-0 flex-1">
                  <strong className="font-medium text-foreground">
                    {ROTULO_TIPO_DE_REFERENCIA[item.tipo]}
                  </strong>
                  {' · '}
                  {[item.autores.join('; '), item.titulo, item.ano]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {item.repetida ? (
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                    já está na lista
                  </span>
                ) : null}
              </li>
            ))}
          </ul>

          {novas.length > 0 ? (
            <Button className="w-full sm:w-auto" onClick={acrescentar} data-acrescentar-referencias="">
              Acrescentar à lista
            </Button>
          ) : (
            <p className="text-xs texto-suave" data-nada-a-acrescentar="">
              Nada novo para acrescentar: {repetidas === 1 ? 'ela já está' : 'todas já estão'} na lista.
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}

/** Uma obra que a busca por título achou, com a fonte de onde ela veio. */
type CandidatoDeReferencia = {
  referencia: Omit<ReferenciaDocumento, 'id'>;
  fonte: 'CROSSREF' | 'OPENLIBRARY';
};

/**
 * A busca pelo **título**: o caminho de quem tem o PDF (ou o livro na estante) e não tem
 * o DOI nem o ISBN à mão — que é a maioria das vezes.
 *
 * As duas fontes são consultadas de uma vez, no servidor: o **Crossref** (artigos, teses
 * e capítulos) e a **Open Library** (livros). Cada resultado diz de onde veio, porque a
 * mesma obra pode voltar das duas e a procedência é parte da conferência.
 *
 * Nada de adivinhar qual é a obra certa: a lista é uma **sugestão**, e quem escolhe é
 * quem escreveu. O que já está na lista vem marcado e não entra de novo.
 *
 * Fica fechada num `<details>` porque o painel das referências já é longo no celular —
 * quem precisa abre, e o que abre ocupa a largura toda.
 */
function CaixaDeBuscarPeloTitulo({
  jaNaLista,
  aoAcrescentar,
}: {
  jaNaLista: ReferenciaDocumento[];
  aoAcrescentar: (referencia: Omit<ReferenciaDocumento, 'id'>) => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [candidatos, setCandidatos] = useState<CandidatoDeReferencia[]>([]);
  const [procurando, setProcurando] = useState(false);
  const [procurou, setProcurou] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');

  const procurar = async () => {
    setProcurando(true);
    setErro('');
    setAviso('');
    setProcurou(true);
    try {
      const resposta = await api.buscarReferencias(titulo);
      setCandidatos(resposta.candidatos);
    } catch (causa) {
      setCandidatos([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível procurar agora.');
    } finally {
      setProcurando(false);
    }
  };

  /*
   * A marca da repetida é recalculada no desenho (e não guardada na busca): a lista muda
   * quando a pessoa acrescenta, e o candidato que acabou de entrar tem de sair na hora.
   */
  const marcados = marcarReferenciasRepetidas(
    /* A fonte viaja junto da referência: a linha da lista diz de onde ela veio. */
    candidatos.map((candidato) => ({ ...candidato.referencia, fonte: candidato.fonte })),
    jaNaLista,
  );

  const acrescentar = (indice: number) => {
    const candidato = candidatos[indice];
    if (!candidato) return;
    aoAcrescentar(candidato.referencia);
    setAviso(`“${candidato.referencia.titulo}” entrou na lista.`);
  };

  return (
    <details className="rounded-lg border border-dashed border-border p-3" data-buscar-pelo-titulo="">
      <summary className="cursor-pointer text-sm font-medium">
        Não tem o DOI nem o ISBN? Procure pelo título
      </summary>

      <div className="space-y-3 pt-3">
        <p className="text-xs leading-relaxed texto-suave">
          Escreva o título como você lembra. O gerador procura no Crossref (artigos, teses e capítulos) e
          na Open Library (livros) ao mesmo tempo — cada resultado diz de onde veio, e quem escolhe é você.
        </p>

        <input
          value={titulo}
          onChange={(evento) => {
            setTitulo(evento.target.value);
            setCandidatos([]);
            setProcurou(false);
            setErro('');
            setAviso('');
          }}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter' && titulo.trim().length >= 3 && !procurando) void procurar();
          }}
          aria-label="Título procurado"
          placeholder="Alfabetizar com tela"
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
        />

        <Button
          className="w-full sm:w-auto"
          disabled={procurando || titulo.trim().length < 3}
          onClick={() => void procurar()}
          data-procurar-referencia=""
        >
          <Search />
          {procurando ? 'Procurando...' : 'Procurar'}
        </Button>

        {erro ? (
          <p className="text-xs text-destructive" data-erro-da-busca="">
            {erro}
          </p>
        ) : null}

        {aviso ? (
          <p className="text-xs texto-suave" data-aviso-da-busca="">
            {aviso}
          </p>
        ) : null}

        {procurou && !procurando && marcados.length === 0 && !erro ? (
          <p className="text-xs leading-relaxed texto-suave" data-nada-encontrado="">
            Não achei nada com esse título. Tente menos palavras (o começo do título) ou outro trecho dele —
            e, se você tem o DOI ou o ISBN, eles acham com mais certeza.
          </p>
        ) : null}

        {marcados.length > 0 ? (
          <div
            className="space-y-2 rounded-md border border-border bg-background/70 p-3"
            data-candidatos=""
          >
            <p className="text-xs font-medium">
              Achei {marcados.length} {marcados.length === 1 ? 'obra' : 'obras'} — confira e escolha:
            </p>
            <ul className="space-y-3 text-xs leading-relaxed texto-suave">
              {marcados.map((item, indice) => (
                <li
                  key={`${item.titulo}-${indice}`}
                  className="space-y-1.5"
                  data-repetida={item.repetida ? 'sim' : 'nao'}
                  data-fonte={item.fonte}
                >
                  <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                    <span className="min-w-0 flex-1">
                      <strong className="font-medium text-foreground">
                        {ROTULO_TIPO_DE_REFERENCIA[item.tipo]}
                      </strong>
                      {' · '}
                      {[item.autores.join('; '), item.titulo, item.ano]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                      {item.fonte === 'CROSSREF' ? 'Crossref' : 'Open Library'}
                    </span>
                    {item.repetida ? (
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                        já está na lista
                      </span>
                    ) : null}
                  </div>

                  {item.repetida ? null : (
                    <Button
                      variant="contorno"
                      className="w-full sm:w-auto"
                      onClick={() => acrescentar(indice)}
                      data-acrescentar-candidato=""
                    >
                      <Plus />
                      Acrescentar esta
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

/**
 * Um campo do cartão da referência.
 *
 * O que muda de um tipo para o outro é o **nome do campo** (o rótulo vem de
 * `CAMPOS_DA_REFERENCIA`, no motor — a tela não inventa nome) e o que ele aceita: autores e o
 * texto livre são de várias linhas, as duas datas são data, o resto é linha simples.
 *
 * Antes isso era uma escada de `if (tipo === 'ARTIGO')` aqui dentro, e cada tipo novo custava
 * um `if` em cada campo. Com a lista no motor, acrescentar um tipo não toca nesta tela.
 */
function CampoDoCartao({
  referencia,
  campo,
  aoMudar,
}: {
  referencia: ReferenciaDocumento;
  campo: CampoDaReferencia;
  aoMudar: (parcial: Partial<ReferenciaDocumento>) => void;
}) {
  const linha =
    'h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9';
  const varias = 'w-full rounded-md border border-input bg-background p-2 text-sm';
  /* Campo de várias linhas ocupa as duas colunas — no celular já é uma só. */
  const largura = campo.linhas ? 'sm:col-span-2' : undefined;

  if (campo.campo === 'autores') {
    return (
      <Campo rotulo={campo.rotulo} dica={campo.dica} className={largura}>
        <textarea
          aria-label={campo.rotulo}
          value={referencia.autores.join('\n')}
          onChange={(evento) =>
            aoMudar({
              autores: evento.target.value
                .split('\n')
                .map((linhaDigitada) => linhaDigitada.trim())
                .filter(Boolean),
            })
          }
          rows={campo.linhas ?? 2}
          className={varias}
        />
      </Campo>
    );
  }

  const valor = referencia[campo.campo] ?? '';

  if (campo.campo === 'texto') {
    return (
      <Campo rotulo={campo.rotulo} dica={campo.dica} className={largura}>
        <textarea
          aria-label={campo.rotulo}
          value={valor}
          onChange={(evento) => aoMudar({ texto: evento.target.value })}
          rows={campo.linhas ?? 3}
          className={varias}
        />
      </Campo>
    );
  }

  if (campo.campo === 'acessoEm' || campo.campo === 'dataPublicacao') {
    return (
      <Campo rotulo={campo.rotulo} dica={campo.dica}>
        <input
          type="date"
          aria-label={campo.rotulo}
          value={valor}
          onChange={(evento) =>
            aoMudar({ [campo.campo]: evento.target.value } as Partial<ReferenciaDocumento>)
          }
          className={linha}
        />
      </Campo>
    );
  }

  return (
    <Campo rotulo={campo.rotulo} dica={campo.dica}>
      <input
        aria-label={campo.rotulo}
        value={valor}
        onChange={(evento) =>
          aoMudar({ [campo.campo]: evento.target.value } as Partial<ReferenciaDocumento>)
        }
        className={linha}
      />
    </Campo>
  );
}

function PainelReferencias({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  /* A ficha mostra a linha **na norma do documento**: o que se lê é o que sai no papel. */
  const norma = normaDoDocumento(documento);
  /* O número provisório de cada obra (o que a lista mostra antes da primeira citação). */
  const provisorios = new Map(
    listaDaNorma(documento.referencias, norma).map((item) => [item.referencia.id, item]),
  );

  const mudarReferencia = (indice: number, parcial: Partial<ReferenciaDocumento>) =>
    editar((atual) => ({
      ...atual,
      referencias: atual.referencias.map((referencia, i) =>
        i === indice ? { ...referencia, ...parcial } : referencia,
      ),
    }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Referências</CardTitle>
        <CardDescription>
          Preencha os campos e o motor monta a referência na NBR 6023: sobrenome em maiúsculas, título em
          negrito, cidade, editora e ano — e a lista sai em ordem alfabética. Você vê o resultado embaixo
          de cada ficha.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* A porta de fora: colar BibTeX, RIS, ISBN ou um DOI (mobile primeiro: caixa cheia). */}
        <CaixaDeImportarReferencias
          jaNaLista={documento.referencias}
          aoAcrescentar={(novas) =>
            editar((atual) => ({
              ...atual,
              referencias: [
                ...atual.referencias,
                /* O id é da tela: o motor devolve a referência sem id, de propósito. */
                ...novas.map((referencia, indice) => ({
                  ...referencia,
                  id: `r-${Date.now().toString(36)}-${indice}`,
                })),
              ],
            }))
          }
        />

        {/* E, para quem não tem o número à mão, a procura pelo título. */}
        <CaixaDeBuscarPeloTitulo
          jaNaLista={documento.referencias}
          aoAcrescentar={(encontrada) =>
            editar((atual) => ({
              ...atual,
              referencias: [
                ...atual.referencias,
                { ...encontrada, id: `r-${Date.now().toString(36)}-busca` },
              ],
            }))
          }
        />

        {documento.referencias.map((referencia, indice) => (
          <section
            key={referencia.id}
            data-ficha-da-referencia={referencia.id}
            className="space-y-3 rounded-lg border border-border p-3"
          >
            <div className="flex flex-wrap items-end gap-2">
              <Select
                value={referencia.tipo}
                onValueChange={(valor) => mudarReferencia(indice, { tipo: valor as ReferenciaDocumento['tipo'] })}
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DE_REFERENCIA.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {ROTULO_TIPO_DE_REFERENCIA[tipo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="fantasma"
                tamanho="pequeno"
                onClick={() =>
                  editar((atual) => ({
                    ...atual,
                    referencias: atual.referencias.filter((_, i) => i !== indice),
                  }))
                }
              >
                <Trash2 />
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {CAMPOS_DA_REFERENCIA[referencia.tipo].map((campo) => (
                <CampoDoCartao
                  key={campo.campo}
                  referencia={referencia}
                  campo={campo}
                  aoMudar={(parcial) => mudarReferencia(indice, parcial)}
                />
              ))}
            </div>

            {normaNumerada(norma) ? (
              <Campo
                rotulo="Número na lista"
                dica="É o número que a citação no texto usa e a ordem em que a lista sai. Ele entra sozinho na primeira vez que você cita a obra aqui no editor."
              >
                <input
                  type="number"
                  min={1}
                  aria-label={`Número na lista de ${referencia.titulo.trim() || 'sem título'}`}
                  value={referencia.numeroDaLista ?? ''}
                  /* O provisório aparece como dica: é o número que a lista já mostra. */
                  placeholder={
                    provisorios.get(referencia.id)?.provisorio
                      ? String(provisorios.get(referencia.id)?.numero ?? '')
                      : ''
                  }
                  onChange={(evento) => {
                    const bruto = Number.parseInt(evento.target.value, 10);
                    mudarReferencia(indice, {
                      numeroDaLista: Number.isFinite(bruto) && bruto > 0 ? bruto : undefined,
                    });
                  }}
                  className="h-10 w-24 rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
            ) : null}

            <p
              data-linha-da-referencia
              className="rounded-md bg-muted/60 p-2 text-xs leading-relaxed"
            >
              {linhaDaReferencia(referencia, norma).replace(/\*\*/g, '')}
            </p>
          </section>
        ))}

        <Button
          variant="contorno"
          onClick={() =>
            editar((atual) => ({
              ...atual,
              referencias: [
                ...atual.referencias,
                {
                  id: `ref-${Date.now().toString(36)}`,
                  tipo: 'LIVRO',
                  autores: [],
                  titulo: '',
                  local: '',
                  ano: '',
                },
              ],
            }))
          }
        >
          <Plus />
          Nova referência
        </Button>
      </CardContent>
    </Card>
  );
}

function PainelImagens({ documento, editar }: { documento: DocumentoAcademico; editar: Editar }) {
  const [enviando, setEnviando] = useState('');

  async function enviar(qual: 'capa' | 'cabecalho', arquivo: File) {
    setEnviando(qual);
    try {
      const { arquivo: enviado } = await api.enviarImagem(arquivo);
      editar((atual) => ({
        ...atual,
        [qual]: { url: enviado.url, alturaMm: atual[qual]?.alturaMm ?? (qual === 'capa' ? 60 : 18) },
      }));
    } finally {
      setEnviando('');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Imagem da capa e do cabeçalho</CardTitle>
        <CardDescription>
          A <strong>capa</strong> entra no alto da primeira folha, acima do texto da capa. O{' '}
          <strong>cabeçalho</strong> se repete no alto de todas as folhas impressas — é a faixa com a
          marca da instituição ou do curso.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {(['capa', 'cabecalho'] as const).map((qual) => (
          <section key={qual} className="space-y-2 rounded-lg border border-border p-3">
            <span className="text-sm font-medium">
              {qual === 'capa' ? 'Imagem da capa' : 'Imagem do cabeçalho'}
            </span>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo rotulo="Arquivo" dica="PNG, JPG ou WEBP, até 8 MB.">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(evento) => {
                    const arquivo = evento.target.files?.[0];
                    if (arquivo) void enviar(qual, arquivo);
                  }}
                  className="block w-full text-xs"
                />
              </Campo>
              <Campo rotulo="Altura impressa (mm)">
                <input
                  type="number"
                  min={8}
                  max={120}
                  value={documento[qual]?.alturaMm ?? (qual === 'capa' ? 60 : 18)}
                  onChange={(evento) =>
                    editar((atual) => ({
                      ...atual,
                      [qual]: atual[qual]
                        ? { ...atual[qual]!, alturaMm: Number(evento.target.value) }
                        : { url: '', alturaMm: Number(evento.target.value) },
                    }))
                  }
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
            </div>

            {enviando === qual ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                Enviando...
              </p>
            ) : null}

            {documento[qual]?.url ? (
              <div className="space-y-2">
                <img
                  src={urlDaImagem(documento[qual]!.url)}
                  alt=""
                  className="max-h-32 rounded border border-border object-contain"
                />
                <Button
                  variant="fantasma"
                  tamanho="pequeno"
                  onClick={() => editar((atual) => ({ ...atual, [qual]: null }))}
                >
                  <Trash2 />
                  Tirar esta imagem
                </Button>
              </div>
            ) : null}
          </section>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * O CSS do corpo do texto **dentro do editor**.
 *
 * Não é o desenho da folha — esse é o papel de baixo, e é o que sai impresso. Isto
 * aqui serve para o rascunho não mentir sobre o formato do texto: mesmo tipo de
 * letra, mesmo corpo, mesma entrada de parágrafo e o mesmo recuo da citação longa.
 *
 * A tabela é a exceção combinada: no editor as células têm contorno leve (é assim
 * que se vê onde escrever), e no papel saem com os filetes de cima e de baixo, que
 * é como a norma desenha tabela.
 */
function cssDoCorpoDoEditor(documento: DocumentoAcademico): string {
  /* A geometria do documento (e não só a do papel): o recuo do parágrafo do
     padrão institucional precisa aparecer no rascunho como sai no papel. */
  const geometria = geometriaDoDocumento(documento);
  const recuado = formatoDeParagrafo(documento) === 'RECUADO';
  /*
   * O recuo de bloco, degrau por degrau. Os valores saem do mesmo lugar do papel
   * impresso (`DEGRAU_DE_RECUO_MM`), então o rascunho não mente sobre o formato — e
   * a citação longa soma o recuo dela (4 cm no A4, 24 mm no A5) ao degrau.
   */
  const regrasDoRecuo = Array.from({ length: degrausDeRecuoDoPapel(geometria.papel) }, (_, indice) => {
    const degrau = (indice + 1) * DEGRAU_DE_RECUO_MM;
    return `
    p[data-recuo="${indice + 1}"], ul[data-recuo="${indice + 1}"], ol[data-recuo="${indice + 1}"] { margin-left: ${degrau}mm; }
    blockquote[data-recuo="${indice + 1}"] { margin-left: ${geometria.recuoCitacaoMm + degrau}mm; }`;
  }).join('');

  return `
    body { font-family: ${familiaDoDocumento(documento)}; font-size: ${geometria.corpoPt}pt; line-height: ${geometria.entrelinha}; color: #111827; }
    p { margin: 0 0 6pt; text-align: justify; text-indent: ${recuado ? `${geometria.recuoParagrafoMm}mm` : '0'}; }
    h1, h2, h3 { font-family: inherit; font-size: ${geometria.corpoPt}pt; font-weight: bold; text-align: left; text-indent: 0; margin: 12pt 0 6pt; }
    blockquote { margin: 0 0 6pt ${geometria.recuoCitacaoMm}mm; font-size: ${geometria.reduzidoPt}pt; line-height: 1.15; }
    ul, ol { margin: 0 0 6pt ${geometria.recuoParagrafoMm}mm; }
    li { margin-bottom: 3pt; }
    table { border-collapse: collapse; width: 100%; }
    td, th { border: 1px solid #d1d5db; padding: 2pt 4pt; text-align: left; }
    th { font-weight: bold; }${regrasDoRecuo}
  `;
}

/**
 * A barra de escrita.
 *
 * Ela é **curta de propósito**. O negrito, o itálico, a lista, a tabela, o localizar
 * e substituir, o desfazer e o refazer são do editor de texto e estão na barra dele,
 * logo abaixo: repetir aqui o botão que faz a mesma coisa só serve para confundir.
 *
 * O que sobra é o que é **nosso**, e não de um editor qualquer:
 *
 * - **Seção nova**: a linha do cursor vira o título de uma seção — e é a partir daí
 *   que vêm a numeração (1, 1.1), a folha nova do capítulo e o sumário;
 * - **Título 1 a 6**: o **nível da seção** em que o cursor está. Nível de seção,
 *   e não formatação de linha, porque no documento acadêmico um título **é** uma
 *   seção (é o que a numeração e o sumário obedecem);
 * - **Aumentar / diminuir recuo**: o **bloco** em que o cursor está (parágrafo, lista
 *   ou citação longa) anda um degrau de 12,5 mm para a direita ou para a esquerda,
 *   como o "Aumentar recuo" do Word. Título não anda — a seção abre na margem —, e o
 *   botão para quando o papel não aceita mais degraus;
 * - **Citar**: a citação autor-data, montada das referências do documento;
 * - **Salvar**, que é de quem escreve.
 */
function BarraDeEscrita({
  livro,
  norma,
  nivelDoCursor,
  recuoDoCursor,
  maximoDeRecuo,
  referencias,
  salvando,
  naoSalvo,
  aoMudarNivel,
  aoNovaSecao,
  aoCitacaoLonga,
  aoAspas,
  aoInserirNota,
  aoInserirImagem,
  proximoNumeroDeNota,
  aoMudarRecuo,
  aoCitar,
  aoSalvar,
}: {
  livro: boolean;
  /** A norma do documento: é ela que escreve a citação do menu **Citar**. */
  norma: NormaDeCitacao;
  /** O nível da seção em que o cursor está (`h1`, `h2`, `h3`). */
  nivelDoCursor: string;
  /**
   * O recuo do bloco em que o cursor está, em degraus; `null` quando ali não há
   * bloco que aceite recuo (um título) — e aí os dois botões ficam apagados.
   */
  recuoDoCursor: number | null;
  /** Quantos degraus o papel aceita (o teto vem do motor, pelo tamanho da mancha). */
  maximoDeRecuo: number;
  /** As referências do documento, na ordem em que estão gravadas (quem ordena é a norma). */
  referencias: ReferenciaDocumento[];
  salvando: boolean;
  naoSalvo: boolean;
  /** Muda o nível da seção em que o cursor está (`h1` a `h6`). */
  aoMudarNivel: (nivel: string) => void;
  /** A linha do cursor vira citação longa (o bloco recuado de 4 cm). */
  aoCitacaoLonga: () => void;
  /** Aspas no trecho selecionado: a citação curta. */
  aoAspas: () => void;
  /** Põe uma nota no cursor, com este número. */
  aoInserirNota: () => void;
  /** Envia a imagem escolhida e a põe no cursor. */
  aoInserirImagem: (arquivo: File) => void;
  /** O número que a próxima nota vai receber (é o que o botão anuncia). */
  proximoNumeroDeNota: number;
  /** A linha do cursor vira o título de uma seção nova. */
  aoNovaSecao: () => void;
  /** Anda o recuo do bloco: `1` para dentro, `-1` para fora. */
  aoMudarRecuo: (passo: 1 | -1) => void;
  aoCitar: (referencia: ReferenciaDocumento, pagina: string) => void;
  aoSalvar: () => void;
}) {
  /** A página da citação (`(SOUZA, 2020, p. 15)`), quando a pessoa escrever uma. */
  const [pagina, setPagina] = useState('');

  /**
   * Um botão da barra.
   *
   * O `mousedown` cancelado não é detalhe de estilo: o texto está dentro do editor, e
   * sem isso o clique tira o foco de lá — a citação entraria no começo do texto em
   * vez de onde o cursor está. É o mesmo cuidado que a barra sempre teve.
   */
  const botao = (
    conteudo: React.ReactNode,
    acao: () => void,
    opcoes: { titulo: string; chave?: string; ativo?: boolean; desabilitado?: boolean },
  ) => (
    <button
      key={opcoes.chave}
      type="button"
      title={opcoes.titulo}
      aria-pressed={opcoes.ativo}
      disabled={opcoes.desabilitado}
      onMouseDown={(evento) => evento.preventDefault()}
      onClick={acao}
      className={[
        'rounded px-2.5 py-1.5 text-sm transition-colors disabled:opacity-40',
        opcoes.ativo ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
      ].join(' ')}
    >
      {conteudo}
    </button>
  );

  const niveis: Array<{ tag: string; rotulo: string }> = [
    { tag: 'h1', rotulo: livro ? 'Capítulo 1' : 'Título 1' },
    { tag: 'h2', rotulo: 'Título 2' },
    { tag: 'h3', rotulo: 'Título 3' },
    { tag: 'h4', rotulo: 'Título 4' },
    { tag: 'h5', rotulo: 'Título 5' },
    { tag: 'h6', rotulo: 'Título 6' },
  ];

  return (
    <div
      data-barra-de-escrita=""
      className="sticky top-0 z-10 flex flex-wrap items-center gap-1 rounded-lg border border-border bg-card p-1.5 shadow-sm"
    >
      {botao(livro ? 'Capítulo novo' : 'Seção nova', aoNovaSecao, {
        titulo: 'A linha do cursor vira o título de uma seção nova',
      })}

      <span className="mx-1 h-5 w-px bg-border" />

      {niveis.map((item) =>
        botao(item.rotulo, () => aoMudarNivel(item.tag), {
          chave: item.tag,
          titulo: `${item.rotulo} — muda o nível da seção em que o cursor está, com a numeração junto`,
          ativo: nivelDoCursor === item.tag,
        }),
      )}

      <span className="mx-1 h-5 w-px bg-border" />

      {/*
        O recuo do bloco: o parágrafo inteiro (ou a lista, ou a citação longa) anda um
        degrau de 12,5 mm para dentro ou para fora — o mesmo gesto do "Aumentar
        recuo" de um editor de textos, e o mesmo número que sai no papel.

        Os dois botões apagam quando o cursor está num título: no documento o título
        **é** a seção, e a seção abre na margem. É melhor o botão apagado do que um
        clique que não faz nada.
      */}
      {botao(<IndentDecrease className="size-4" />, () => aoMudarRecuo(-1), {
        chave: 'diminuir-recuo',
        titulo: 'Diminuir recuo — o bloco volta um degrau para a esquerda (12,5 mm)',
        desabilitado: recuoDoCursor === null || recuoDoCursor === 0,
      })}
      {botao(<IndentIncrease className="size-4" />, () => aoMudarRecuo(1), {
        chave: 'aumentar-recuo',
        titulo: `Aumentar recuo — o bloco anda um degrau para a direita (12,5 mm), até ${maximoDeRecuo}`,
        desabilitado: recuoDoCursor === null || recuoDoCursor >= maximoDeRecuo,
      })}

      <span className="mx-1 h-5 w-px bg-border" />

      {/*
        A **citação longa** (NBR 10520): o parágrafo do cursor passa a sair recuado 4 cm, em
        corpo menor e espaço simples — a forma da citação com mais de três linhas.
      */}
      /*
       * Citação longa, aspas e nota são coisas **do texto**, e não do título: no documento o
       * título **é** a seção (a linha que numera, abre folha e entra no sumário). Com o cursor
       * nele, os três botões apagam — melhor o botão apagado do que um clique que não faz nada
       * (ou que estraga o título).
       */
      {botao('Citação longa', aoCitacaoLonga, {
        titulo: 'O parágrafo do cursor sai recuado 4 cm, em corpo menor e espaço simples (mais de três linhas)',
        desabilitado: recuoDoCursor === null,
      })}

      {/* A citação curta: aspas no trecho selecionado, correndo no meio do texto. */}
      {botao('“ ”', aoAspas, {
        titulo: 'Aspas no trecho selecionado — a citação curta, que corre no texto',
        desabilitado: recuoDoCursor === null,
      })}

      {/*
        A **nota**: no texto sai o número, e o texto da nota se escreve no painel (é lá que
        ele tem lugar). Ela leva o próximo número livre.
      */}
      {botao('Nota', aoInserirNota, {
        titulo: `Nota com o número ${proximoNumeroDeNota} — o texto dela se escreve no painel "Notas"`,
        desabilitado: recuoDoCursor === null,
      })}

      {/*
        A **figura**: escolhe o arquivo, envia (o mesmo caminho das imagens de capa) e põe a
        imagem no lugar do cursor. A legenda e a fonte se escrevem no painel "Figuras".
      */}
      <label
        title="Imagem no texto — a legenda e a fonte se escrevem no painel Figuras"
        className={[
          'cursor-pointer rounded px-2.5 py-1.5 text-sm transition-colors',
          recuoDoCursor === null ? 'cursor-not-allowed opacity-40' : 'hover:bg-muted',
        ].join(' ')}
      >
        Imagem
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          aria-label="Enviar imagem para o texto"
          disabled={recuoDoCursor === null}
          className="hidden"
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            evento.target.value = '';
            if (arquivo) aoInserirImagem(arquivo);
          }}
        />
      </label>

      <span className="mx-1 h-5 w-px bg-border" />

      {/*
        Citar: a lista das referências do documento, com a citação que cada uma vira.
        Clicar **insere no cursor** — e o `mousedown` cancelado é o que mantém o foco
        no editor, sem o qual a citação cairia no lugar errado.
      */}
      <Menu rotulo="Citar" largura="min(92vw, 30rem)" tom="contorno">
        {({ fechar }) => (
          <>
            <GrupoDoMenu titulo="Citação no texto (NBR 10520)">
              <p className="text-xs leading-relaxed texto-suave">
                Escolha a obra: a citação entra onde o cursor está, com o sobrenome que a lista de
                referências imprime.
              </p>
              <Campo
                rotulo="Página (opcional)"
                dica="Só quando a citação for de uma página: sai como (SOUZA, 2020, p. 15)."
              >
                <input
                  aria-label="Página da citação"
                  value={pagina}
                  onChange={(evento) => setPagina(evento.target.value)}
                  placeholder="15"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
            </GrupoDoMenu>

            {referencias.length === 0 ? (
              <p className="text-sm texto-suave">
                Nenhuma referência cadastrada ainda. Em <strong>Documento → Referências</strong>, cadastre
                a obra e quem escreveu: a citação sai daí, com o sobrenome que a lista imprime.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {listaDaNorma(referencias, norma).map((item) => {
                  /*
                   * A citação sai **com o número** (nas normas numeradas): o número da obra
                   * ou, se ela ainda não foi citada, o provisório — que é o que a citação vai
                   * gravar. Assim o que a pessoa lê no menu é o que entra no texto.
                   */
                  const texto = citacaoDaReferencia(
                    item.numero
                      ? { ...item.referencia, numeroDaLista: item.numero }
                      : item.referencia,
                    norma,
                    pagina,
                  );
                  return (
                    <li key={item.referencia.id}>
                      <button
                        type="button"
                        onMouseDown={(evento) => evento.preventDefault()}
                        onClick={() => {
                          aoCitar(item.referencia, pagina);
                          /*
                           * O menu fecha ao citar: a citação já entrou, e o painel aberto por
                           * cima da barra é o que atrapalhava o clique seguinte (ele cobre os
                           * botões quando a barra quebra em duas linhas, no celular).
                           */
                          fechar();
                        }}
                        disabled={!texto}
                        className="w-full space-y-0.5 rounded px-2 py-2 text-left transition-colors hover:bg-muted disabled:opacity-40"
                      >
                        <span className="block text-sm font-medium">
                          {texto || (normaNumerada(norma) ? 'Sem número ainda' : 'Sem autor ou sem ano')}
                        </span>
                        <span className="block text-xs texto-suave">
                          {item.referencia.titulo.trim() ||
                            item.referencia.texto?.trim() ||
                            'Sem título'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </Menu>

      <span className="ml-auto flex items-center gap-2">
        {naoSalvo ? <Badge tom="destaque">Mudanças não salvas</Badge> : null}
        {botao(salvando ? 'Salvando...' : 'Salvar', aoSalvar, {
          titulo: 'Salvar o documento',
          desabilitado: salvando,
        })}
      </span>
    </div>
  );
}
